import React, { useState, useMemo, useEffect, useRef } from "react";
import { ref, uploadString, getDownloadURL } from "firebase/storage";
import JSZip from "jszip";
import * as XLSX from "xlsx";
import CardMobile from "./CardMobile";

// Uma cor própria por Tipo de Limpeza, usada no badge (mobile e desktop).
const CORES_TIPO_LIMPEZA = {
  "Checkout Airbnb": { bg: "#dbeafe", cor: "#1e40af" },
  "Checkout Semestre": { bg: "#ede9fe", cor: "#6d28d9" },
  "Preparação Quarto": { bg: "#fef9c3", cor: "#854d0e" },
  "Limpeza Extra": { bg: "#fce7f3", cor: "#be185d" },
  "Mudança Roupas": { bg: "#e0f2fe", cor: "#0369a1" },
  Manutenção: { bg: "#dcfce7", cor: "#15803d" },
};
const COR_TIPO_LIMPEZA_PADRAO = { bg: "#f1f5f9", cor: "#64748b" };

export default function EscalaLimpeza({
  estadias,
  isAdmin,
  notasLimpeza,
  prioridades,
  tipoCheckout,
  numInquilinos,
  tipoQuarto,
  tipoPreparacao,
  limpezasExtra,
  onNotaChange,
  onPrioridadeToggle,
  onTipoCheckoutChange,
  onNumInquilinosChange,
  onTipoQuartoChange,
  onTipoPreparacaoChange,
  onAdicionarExtra,
  onRemoverExtra,
  reportsOlympus,
  onReportOlympusChange,
  precosMatriz,
  onPrecoMatrizChange,
  configAreasComuns,
  onConfigAreaComumChange,
  limpezasOcultas,
  onLimpezasOcultasChange,
  onObservacaoInternaChange,
  onDataFaturaChange,
  onPrecoBaseManualChange,
  storage,
  onCarregarReportsAntigos,
  aCarregarReportsAntigos,
  historicoAntigoCarregado,
}) {
  // =========================================================
  // 1. ESTADOS E VARIÁVEIS DE CONTROLO
  // =========================================================
  const [subAbaLimpezas, setSubAbaLimpezas] = useState("ativa");
  const [periodoLimpeza, setPeriodoLimpeza] = useState("proximos");

  // Filtros de Intervalo de Tempo Personalizado
  const [dataInicioPersonalizada, setDataInicioPersonalizada] = useState("");
  const [dataFimPersonalizada, setDataFimPersonalizada] = useState("");

  // Filtros Avançados Partilhados (Aplicados nas 3 abas)
  const [filtroAptsSelecionados, setFiltroAptsSelecionados] = useState([]);
  const [filtroQuarto, setFiltroQuarto] = useState("");
  const [filtroEstadoLimpo, setFiltroEstadoLimpo] = useState(""); // "" (todos), "sim", "pendente"
  const [filtroTipoCheckout, setFiltroTipoCheckout] = useState(""); // "" (todos), ou os tipos textuais
  const [filtroMesAtiva, setFiltroMesAtiva] = useState("");
  const [filtroInquilinoNome, setFiltroInquilinoNome] = useState("");

  // Filtros Específicos do Report de Limpezas
  const [filtroEstadoOlympus, setFiltroEstadoOlympus] = useState("");
  const [apenasComAvisos, setApenasComAvisos] = useState(false);
  const [filtroMesFinanceiro, setFiltroMesFinanceiro] = useState("");

  // Estados de Ordenação (Sort)
  const [sortAtivaColuna, setSortAtivaColuna] = useState("data");
  const [sortAtivaAsc, setSortAtivaAsc] = useState(true);
  const [sortReportColuna, setSortReportColuna] = useState("data");
  const [sortReportAsc, setSortReportAsc] = useState(true);
  const [sortFinColuna, setSortFinColuna] = useState("data");
  const [sortFinAsc, setSortFinAsc] = useState(true);

  // Modais e Lightbox
  const [modalAberto, setModalAberto] = useState(false);
  const [modalItemChave, setModalItemChave] = useState(null);
  const [modalItemData, setModalItemData] = useState("");
  const [modalReportTexto, setModalReportTexto] = useState("");
  const [modalFotosAntes, setModalFotosAntes] = useState([]);
  const [modalFotosDepois, setModalFotosDepois] = useState([]);
  const [modalCustoExtra, setModalCustoExtra] = useState("");
  const [lightboxAberto, setLightboxAberto] = useState(false);
  const [lightboxListaFotos, setLightboxListaFotos] = useState([]);
  const [lightboxIndexAtivo, setLightboxIndexAtivo] = useState(0);
  const [lightboxTitulo, setLightboxTitulo] = useState("");
  const touchStartXRef = useRef(null);

  // Estados Temporários (só para os campos de texto/número, que gravam com
  // um pequeno atraso depois de a pessoa parar de escrever — ver
  // salvarComDebounce). Todos os outros campos (select, botões) gravam
  // imediatamente na Firestore, sem passar por aqui.
  const [tempInquilinos, setTempInquilinos] = useState({});
  const [tempQuartos, setTempQuartos] = useState({});
  const [tempNotas, setTempNotas] = useState({});
  const [tempObsInterna, setTempObsInterna] = useState({});
  const [tempDataFatura, setTempDataFatura] = useState({});
  const [tempPrecoBaseManual, setTempPrecoBaseManual] = useState({});
  const [mensagemSucesso, setMensagemSucesso] = useState("");
  const debounceInquilinosRef = useRef({});
  const debounceNotasRef = useRef({});
  const debounceObsInternaRef = useRef({});
  const debounceDataFaturaRef = useRef({});
  const debouncePrecoBaseManualRef = useRef({});

  // Grava na cloud automaticamente, um pouco depois de a pessoa parar de
  // escrever, para não disparar uma escrita a cada letra digitada.
  const salvarComDebounce = (timersRef, chave, valor, funcaoSalvar) => {
    if (timersRef.current[chave]) clearTimeout(timersRef.current[chave]);
    timersRef.current[chave] = setTimeout(() => {
      funcaoSalvar(chave, valor);
      delete timersRef.current[chave];
    }, 900);
  };

  // Formata um Date como "aaaa-mm-dd" para pré-preencher inputs type="date".
  const paraInputDate = (d) => {
    if (!d) return "";
    const ano = d.getFullYear();
    const mes = String(d.getMonth() + 1).padStart(2, "0");
    const dia = String(d.getDate()).padStart(2, "0");
    return `${ano}-${mes}-${dia}`;
  };

  // Formulário Manual
  const [extraTipoLimpeza, setExtraTipoLimpeza] = useState("");
  const [extraApt, setExtraApt] = useState("");
  const [extraQuarto, setExtraQuarto] = useState("");
  const [extraDescricaoTarefa, setExtraDescricaoTarefa] = useState("");
  const [extraData, setExtraData] = useState("");

  // =========================================================
  // 2. MOTORES DE FILTRAGEM INTELIGENTES E SEGUROS
  // =========================================================

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxAberto) return;
      if (e.key === "Escape") fecharLightbox();
      if (e.key === "ArrowRight") passarProximaFoto();
      if (e.key === "ArrowLeft") voltarFotoAnterior();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [lightboxAberto, lightboxListaFotos, lightboxIndexAtivo]);

  const handleToggleOcultarLimpeza = (idChave) => {
    const novoArray = limpezasOcultas.includes(idChave)
      ? limpezasOcultas.filter((id) => id !== idChave)
      : [...limpezasOcultas, idChave];
    onLimpezasOcultasChange(novoArray);
    setMensagemSucesso("✔️ Visibilidade atualizada!");
    setTimeout(() => setMensagemSucesso(""), 1500);
  };

  const obterNomeDiaSemana = (dataObj) => {
    const dias = [
      "Domingo",
      "Segunda",
      "Terça",
      "Quarta",
      "Quinta",
      "Sexta",
      "Sábado",
    ];
    return dias[dataObj.getDay()];
  };

  const todasLimpezasPossiveis = useMemo(() => {
    const normais = estadias.map((item) => {
      const aptLimpo = String(item.apt)
        .replace(/[\s#]+/g, "")
        .toUpperCase();
      const quartoLimpo = String(item.quarto)
        .replace(/[\s#]+/g, "")
        .toUpperCase();

      let proximaEstadia = null;
      if (item.dataSaidaEfetivaObj) {
        const dataSaidaAtual = new Date(item.dataSaidaEfetivaObj);

        const candidatas = estadias.filter((e) => {
          const eApt = String(e.apt)
            .replace(/[\s#]+/g, "")
            .toUpperCase();
          const eQuarto = String(e.quarto)
            .replace(/[\s#]+/g, "")
            .toUpperCase();

          return (
            eApt === aptLimpo &&
            eQuarto === quartoLimpo &&
            e.id !== item.id &&
            e.dataEntradaEfetivaObj
          );
        });

        candidatas.sort(
          (a, b) =>
            new Date(a.dataEntradaEfetivaObj) -
            new Date(b.dataEntradaEfetivaObj)
        );

        proximaEstadia = candidatas.find(
          (e) => new Date(e.dataEntradaEfetivaObj) >= dataSaidaAtual
        );

        if (proximaEstadia) {
          const dataEntradaProxima = new Date(
            proximaEstadia.dataEntradaEfetivaObj
          );
          const diferencaTempo =
            dataEntradaProxima.getTime() - dataSaidaAtual.getTime();
          const diferencaDias = Math.ceil(
            diferencaTempo / (1000 * 60 * 60 * 24)
          );

          const plat = String(proximaEstadia.plataforma || "").toLowerCase();
          const ehSemestre = !plat.includes("airbnb");

          if (diferencaDias > 6 && ehSemestre) {
            item.ALERTA_GAP_LONGO = true;
            item.DIAS_GAP_LONGO = diferencaDias;
          }
        }
      }

      let preparacaoSugerida = "";
      let inquilinosSugeridos = "";

      if (proximaEstadia) {
        inquilinosSugeridos =
          proximaEstadia.numeroInquilinos || proximaEstadia.numInquilinos || "";

        const plat = String(proximaEstadia.plataforma || "").toLowerCase();
        if (plat.includes("airbnb")) {
          preparacaoSugerida = "Airbnb";
        } else {
          preparacaoSugerida = "Semestre";
        }
      }

      // Durante um GAP longo o quarto fica "aberto" no calendário do Airbnb
      // para tentar apanhar uma reserva entretanto — por isso a preparação
      // sugerida é sempre Airbnb aqui, mesmo que a próxima reserva
      // confirmada seja de Semestre.
      if (item.ALERTA_GAP_LONGO) {
        preparacaoSugerida = "Airbnb";
      }

      const dataStr = item.dataSaidaEfetivaObj
        ? item.dataSaidaEfetivaObj.toISOString().split("T")[0]
        : "DATA_DESCONHECIDA";

      return {
        ...item,
        isExtra: false,
        idChave: `${aptLimpo}_${quartoLimpo}_${dataStr}`,
        isAreaComum: false,
        preparacaoAutomatica: preparacaoSugerida,
        inquilinosAutomaticos: inquilinosSugeridos,
        dataCheckinFutura: proximaEstadia
          ? proximaEstadia.entradaEfetivaTexto || "-"
          : "Livre",
      };
    });

    const extras = limpezasExtra.map((item) => {
      let dSaidaObj = new Date();
      if (item.dataSaida) {
        const partes = item.dataSaida.split("-");
        if (partes.length === 3)
          dSaidaObj = new Date(
            parseInt(partes[0]),
            parseInt(partes[1]) - 1,
            parseInt(partes[2])
          );
        else dSaidaObj = new Date(item.dataSaida);
      }
      dSaidaObj.setHours(0, 0, 0, 0);
      const mesesLista = [
        "jan.",
        "fev.",
        "mar.",
        "abr.",
        "mai.",
        "jun.",
        "jul.",
        "ago.",
        "set.",
        "out.",
        "nov.",
        "dez.",
      ];

      return {
        id: item.idCol || item.id,
        idCol: item.idCol || item.id,
        apt: item.apt,
        quarto: item.quarto,
        nome: item.nome || "Preparação Quarto",
        plataforma: "EXTRA",
        saidaEfetivaTexto: `${dSaidaObj.getDate()} ${
          mesesLista[dSaidaObj.getMonth()]
        } ${dSaidaObj.getFullYear()}`,
        dataSaidaEfetivaObj: dSaidaObj,
        isExtra: true,
        idChave: `EXTRA_${item.idCol || item.id}`,
        isAreaComum: false,
      };
    });

    const eventosAreasComuns = [];
    const listaApts = Object.keys(configAreasComuns);

    const inicioProjecao = new Date();
    inicioProjecao.setDate(inicioProjecao.getDate() - 10);
    const fimProjecao = new Date();
    fimProjecao.setDate(fimProjecao.getDate() + 25);

    for (
      let d = new Date(inicioProjecao);
      d <= fimProjecao;
      d.setDate(d.getDate() + 1)
    ) {
      const dObj = new Date(d);
      dObj.setHours(0, 0, 0, 0);
      const diaNomeStr = obterNomeDiaSemana(dObj);
      const mesesLista = [
        "jan.",
        "fev.",
        "mar.",
        "abr.",
        "mai.",
        "jun.",
        "jul.",
        "ago.",
        "set.",
        "out.",
        "nov.",
        "dez.",
      ];

      listaApts.forEach((aptChave) => {
        const configApt = configAreasComuns[aptChave];
        if (configApt && configApt.dias.includes(diaNomeStr)) {
          const idChaveAC = `AREA_COMUM_${aptChave}_${
            dObj.toISOString().split("T")[0]
          }`;
          eventosAreasComuns.push({
            idChave: idChaveAC,
            apt: `${aptChave}#`,
            quarto: "Áreas Comuns",
            nome: "Limpeza de Rotina",
            plataforma: "OLYMPUS",
            saidaEfetivaTexto: `${dObj.getDate()} ${
              mesesLista[dObj.getMonth()]
            } ${dObj.getFullYear()}`,
            dataSaidaEfetivaObj: dObj,
            isExtra: false,
            isAreaComum: true,
          });
        }
      });
    }
    return [...normais, ...extras, ...eventosAreasComuns];
  }, [estadias, limpezasExtra, configAreasComuns]);

  const filtrarPorPeriodoBase = (itemOuData, tipoBotao) => {
    if (!itemOuData) return false;

    let rawDate = itemOuData;
    if (typeof itemOuData === "object" && itemOuData !== null) {
      rawDate =
        itemOuData.dataSaidaEfetivaObj ||
        itemOuData.dataLimpeza ||
        itemOuData.data ||
        itemOuData.dataSaidaEfetiva ||
        itemOuData.saidaEfetivaTexto;
    }

    if (!rawDate) return false;

    const dLimpeza = new Date(rawDate);
    if (isNaN(dLimpeza.getTime())) return false;

    dLimpeza.setHours(0, 0, 0, 0);

    if (dataInicioPersonalizada || dataFimPersonalizada) {
      if (dataInicioPersonalizada) {
        const dInicio = new Date(dataInicioPersonalizada);
        dInicio.setHours(0, 0, 0, 0);
        if (dLimpeza < dInicio) return false;
      }
      if (dataFimPersonalizada) {
        const dFim = new Date(dataFimPersonalizada);
        dFim.setHours(0, 0, 0, 0);
        if (dLimpeza > dFim) return false;
      }
      return true;
    }

    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);

    const limiteFuturo = new Date(hoje);
    limiteFuturo.setDate(hoje.getDate() + 3);
    limiteFuturo.setHours(23, 59, 59, 999);

    if (
      tipoBotao === "proximos" ||
      tipoBotao === "3dias" ||
      tipoBotao === "hoje_3dias"
    ) {
      return dLimpeza >= hoje && dLimpeza <= limiteFuturo;
    }

    if (tipoBotao === "hoje_frente") {
      return dLimpeza >= hoje;
    }

    if (tipoBotao === "passadas") {
      return dLimpeza < hoje;
    }

    return true;
  };

  const dadosFiltradosGlobais = useMemo(() => {
    const baseLimpezas = todasLimpezasPossiveis || [];

    return baseLimpezas.filter((item) => {
      const idDoItem = item.idChave || item.id || item.idCol;
      const estaMarcadoOculto = limpezasOcultas.includes(idDoItem);

      const urlParams = new URLSearchParams(window.location.search);
      const ehRealmenteAdmin = urlParams.get("role") === "admin" || isAdmin;

      if (!ehRealmenteAdmin && estaMarcadoOculto) {
        return false;
      }

      if (filtroInquilinoNome && filtroInquilinoNome.trim() !== "") {
        const termo = filtroInquilinoNome.toLowerCase().trim();
        if (item.isAreaComum) {
          if (!"área comum".includes(termo)) return false;
        } else if (item.isExtra) {
          if (!"limpeza manual".includes(termo)) return false;
        } else {
          if (!item.nome || !item.nome.toLowerCase().includes(termo))
            return false;
        }
      }

      if (dataInicioPersonalizada || dataFimPersonalizada) {
        if (!item.dataSaidaEfetivaObj) return false;
        const dLimpeza = new Date(item.dataSaidaEfetivaObj);
        dLimpeza.setHours(0, 0, 0, 0);

        if (dataInicioPersonalizada) {
          const dInicio = new Date(dataInicioPersonalizada);
          dInicio.setHours(0, 0, 0, 0);
          if (dLimpeza < dInicio) return false;
        }
        if (dataFimPersonalizada) {
          const dFim = new Date(dataFimPersonalizada);
          dFim.setHours(0, 0, 0, 0);
          if (dLimpeza > dFim) return false;
        }
      } else {
        if (!filtrarPorPeriodoBase(item, periodoLimpeza)) {
          return false;
        }
      }

      const aptPuroItem = String(item.apt || "")
        .replace(/[\s#]+/g, "")
        .toUpperCase();
      if (
        filtroAptsSelecionados &&
        filtroAptsSelecionados.length > 0 &&
        !filtroAptsSelecionados.includes(aptPuroItem)
      ) {
        return false;
      }

      if (
        filtroQuarto &&
        filtroQuarto.trim() !== "" &&
        !String(item.quarto || "")
          .toLowerCase()
          .includes(filtroQuarto.toLowerCase().trim())
      ) {
        return false;
      }

      if (
        filtroMesAtiva &&
        filtroMesAtiva !== "-- Todos --" &&
        filtroMesAtiva !== "Todos os Meses" &&
        filtroMesAtiva !== "" &&
        item.dataSaidaEfetivaObj
      ) {
        const d = new Date(item.dataSaidaEfetivaObj);
        const mesSoNum = String(d.getMonth() + 1).padStart(2, "0");
        const mesAno = `${d.getFullYear()}-${mesSoNum}`;

        if (filtroMesAtiva.includes("-")) {
          if (mesAno !== filtroMesAtiva) return false;
        } else {
          if (mesSoNum !== filtroMesAtiva) return false;
        }
      }

      if (filtroEstadoLimpo && filtroEstadoLimpo !== "") {
        const doc = reportsOlympus[item.idChave] || {};
        const estaLimpo = !!doc.limpo;
        if (filtroEstadoLimpo === "sim" && !estaLimpo) return false;
        if (filtroEstadoLimpo === "pendente" && estaLimpo) return false;
      }

      if (
        filtroTipoCheckout &&
        filtroTipoCheckout !== "" &&
        filtroTipoCheckout !== "-- Todos --"
      ) {
        const tipoEditado = tipoCheckout[item.idChave];

        const tipoPadrao =
          item.isAreaComum ||
          item.isManutencao ||
          String(item.descricao || "")
            .toLowerCase()
            .includes("manuten")
            ? "Manutenção"
            : item.isExtra
            ? "Limpeza Extra"
            : item.isMudancaRoupas ||
              String(item.descricao || "")
                .toLowerCase()
                .includes("roupa")
            ? "Mudança Roupas"
            : String(item.plataforma || "")
                .trim()
                .toUpperCase() === "AIRBNB"
            ? "Checkout Airbnb"
            : "Checkout Semestre";

        const tipoEfetivo =
          tipoEditado || item.tipoLimpeza || item.tipoCheckout || tipoPadrao;

        if (tipoEfetivo !== filtroTipoCheckout) {
          return false;
        }
      }

      return true;
    });
  }, [
    todasLimpezasPossiveis,
    limpezasOcultas,
    isAdmin,
    periodoLimpeza,
    filtroInquilinoNome,
    filtroAptsSelecionados,
    filtroQuarto,
    filtroMesAtiva,
    filtroEstadoLimpo,
    filtroTipoCheckout,
    dataInicioPersonalizada,
    dataFimPersonalizada,
    reportsOlympus,
    tipoCheckout,
  ]);

  const dadosOrdenadosAtiva = useMemo(() => {
    const lista = [...(dadosFiltradosGlobais || [])];

    return lista.sort((a, b) => {
      let vA, vB;

      if (sortAtivaColuna === "apt") {
        vA = (a.apt || "").toLowerCase();
        vB = (b.apt || "").toLowerCase();
      } else if (sortAtivaColuna === "quarto") {
        vA = a.isAreaComum ? 999 : Number(a.quarto) || 0;
        vB = b.isAreaComum ? 999 : Number(b.quarto) || 0;
      } else if (sortAtivaColuna === "inquilino") {
        vA = a.isAreaComum ? "área comum" : (a.nome || "").toLowerCase();
        vB = b.isAreaComum ? "área comum" : (b.nome || "").toLowerCase();
      } else if (sortAtivaColuna === "inquilinos_num") {
        vA = Number(tempInquilinos[a.idChave] || numInquilinos[a.idChave] || 0);
        vB = Number(tempInquilinos[b.idChave] || numInquilinos[b.idChave] || 0);
      } else {
        vA = a.dataSaidaEfetivaObj
          ? new Date(a.dataSaidaEfetivaObj).getTime()
          : 0;
        vB = b.dataSaidaEfetivaObj
          ? new Date(b.dataSaidaEfetivaObj).getTime()
          : 0;
      }

      if (vA < vB) return sortAtivaAsc ? -1 : 1;
      if (vA > vB) return sortAtivaAsc ? 1 : -1;
      return 0;
    });
  }, [
    dadosFiltradosGlobais,
    sortAtivaColuna,
    sortAtivaAsc,
    tempInquilinos,
    numInquilinos,
  ]);

  // Aba "Estadias GAP Longo": mesma lógica de filtros que o resto da app,
  // incluindo o Período (Próximos 3 Dias, Hoje para a Frente, etc.), para
  // se comportar de forma consistente com as outras abas.
  const dadosGapLongoOrdenados = useMemo(() => {
    const base = (todasLimpezasPossiveis || []).filter((item) => {
      if (!item.ALERTA_GAP_LONGO) return false;

      const aptPuroItem = String(item.apt || "")
        .replace(/[\s#]+/g, "")
        .toUpperCase();
      if (
        filtroAptsSelecionados &&
        filtroAptsSelecionados.length > 0 &&
        !filtroAptsSelecionados.includes(aptPuroItem)
      ) {
        return false;
      }
      if (
        filtroQuarto &&
        filtroQuarto.trim() !== "" &&
        !String(item.quarto || "")
          .toLowerCase()
          .includes(filtroQuarto.toLowerCase().trim())
      ) {
        return false;
      }

      if (dataInicioPersonalizada || dataFimPersonalizada) {
        if (!item.dataSaidaEfetivaObj) return false;
        const dLimpeza = new Date(item.dataSaidaEfetivaObj);
        dLimpeza.setHours(0, 0, 0, 0);

        if (dataInicioPersonalizada) {
          const dInicio = new Date(dataInicioPersonalizada);
          dInicio.setHours(0, 0, 0, 0);
          if (dLimpeza < dInicio) return false;
        }
        if (dataFimPersonalizada) {
          const dFim = new Date(dataFimPersonalizada);
          dFim.setHours(0, 0, 0, 0);
          if (dLimpeza > dFim) return false;
        }
      } else if (!filtrarPorPeriodoBase(item, periodoLimpeza)) {
        return false;
      }

      return true;
    });

    return base.sort((a, b) => {
      const vA = a.dataSaidaEfetivaObj
        ? new Date(a.dataSaidaEfetivaObj).getTime()
        : 0;
      const vB = b.dataSaidaEfetivaObj
        ? new Date(b.dataSaidaEfetivaObj).getTime()
        : 0;
      return vA - vB;
    });
  }, [
    todasLimpezasPossiveis,
    filtroAptsSelecionados,
    filtroQuarto,
    periodoLimpeza,
    dataInicioPersonalizada,
    dataFimPersonalizada,
  ]);

  const dadosFiltradosReport = useMemo(() => {
    return dadosFiltradosGlobais.filter((item) => {
      const dataDoc = reportsOlympus[item.idChave] || {};
      if (
        apenasComAvisos &&
        !(
          dataDoc.comentario?.trim() ||
          dataDoc.fotosAntes?.length ||
          dataDoc.fotosDepois?.length
        )
      )
        return false;
      const estadoTexto = dataDoc.limpo ? "Limpo" : "Pendente";
      if (filtroEstadoOlympus && estadoTexto !== filtroEstadoOlympus)
        return false;
      return true;
    });
  }, [
    dadosFiltradosGlobais,
    reportsOlympus,
    apenasComAvisos,
    filtroEstadoOlympus,
  ]);

  const dadosOrdenadosReport = useMemo(() => {
    return [...dadosFiltradosReport].sort((a, b) => {
      let vA, vB;
      if (sortReportColuna === "apt") {
        vA = a.apt.toLowerCase();
        vB = b.apt.toLowerCase();
      } else if (sortReportColuna === "quarto") {
        vA = String(a.quarto);
        vB = String(b.quarto);
      } else if (sortReportColuna === "inquilino") {
        vA = (a.nome || "").toLowerCase();
        vB = (b.nome || "").toLowerCase();
      } else {
        vA = a.dataSaidaEfetivaObj ? a.dataSaidaEfetivaObj.getTime() : 0;
        vB = b.dataSaidaEfetivaObj ? b.dataSaidaEfetivaObj.getTime() : 0;
      }
      return vA < vB
        ? sortReportAsc
          ? -1
          : 1
        : vA > vB
        ? sortReportAsc
          ? 1
          : -1
        : 0;
    });
  }, [dadosFiltradosReport, sortReportColuna, sortReportAsc]);

  const dadosFinanceirosDetalhados = useMemo(() => {
    const listagemFaturavel = [];

    todasLimpezasPossiveis.forEach((item) => {
      if (item.isAreaComum) return;
      const dataDoc = reportsOlympus[item.idChave] || {};
      if (!dataDoc.limpo) return;

      let precoBaseMatriz = 0;
      let cobrancaExtraDigitada = parseFloat(dataDoc.custoExtra) || 0;
      let tQuarto = "";
      let tCheckout = "";

      if (!item.isExtra) {
        const chaveInventarioGlobal = `${String(item.apt)
          .toUpperCase()
          .replace(/[\s#]+/g, "")}_${String(item.quarto)
          .toUpperCase()
          .replace(/[\s#]+/g, "")}`;
        tQuarto =
          tempQuartos[item.idChave] ||
          tipoQuarto[chaveInventarioGlobal] ||
          "Simples";
        const isAirbnbOrigem =
          String(item.plataforma).trim().toUpperCase() === "AIRBNB";
        const checkoutAutomatico = isAirbnbOrigem
          ? "Checkout Airbnb"
          : "Checkout Semestre";
        tCheckout = tipoCheckout[item.idChave] || checkoutAutomatico;

        precoBaseMatriz = precosMatriz[`${tQuarto}_${tCheckout}`] || 0;
      }

      // O Preço Base pode ser ajustado manualmente por limpeza (para mais
      // ou para menos), quando o valor da matriz não se aplica a este caso
      // específico — por defeito usa o valor automático da matriz.
      const precoBaseManual =
        dataDoc.precoBaseManual !== undefined ? dataDoc.precoBaseManual : "";
      const precoBaseEfetivo =
        precoBaseManual !== "" ? parseFloat(precoBaseManual) || 0 : precoBaseMatriz;

      // A "Data Fatura" pode ser corrigida manualmente quando a Olympus
      // fatura a limpeza num período diferente do checkout em si — por
      // defeito usa a data de saída efetiva da estadia.
      const dataFaturaManual = dataDoc.dataFatura || "";
      const dataFaturaObj = dataFaturaManual
        ? new Date(`${dataFaturaManual}T00:00:00`)
        : item.dataSaidaEfetivaObj;

      let mesAnoChave = "";
      if (dataFaturaObj) {
        mesAnoChave = `${dataFaturaObj.getFullYear()}-${String(
          dataFaturaObj.getMonth() + 1
        ).padStart(2, "0")}`;
      }

      listagemFaturavel.push({
        ...item,
        precoBaseMatriz,
        precoBaseManual,
        precoBaseEfetivo,
        cobrancaExtraDigitada,
        custoCalculado: precoBaseEfetivo + cobrancaExtraDigitada,
        dataFaturaManual,
        dataFaturaObj,
        mesAnoChave,
        tipoServicoRealizado: item.isExtra
          ? `Extra: ${item.nome}`
          : `${tQuarto} + ${tCheckout}`,
      });
    });

    return listagemFaturavel;
  }, [
    todasLimpezasPossiveis,
    reportsOlympus,
    tempQuartos,
    tipoQuarto,
    tipoCheckout,
    precosMatriz,
  ]);

  const dadosFinanceirosFiltrados = useMemo(() => {
    return dadosFinanceirosDetalhados.filter((item) => {
      if (dataInicioPersonalizada || dataFimPersonalizada) {
        if (!item.dataFaturaObj) return false;
        const dLimpeza = new Date(item.dataFaturaObj);
        dLimpeza.setHours(0, 0, 0, 0);

        if (dataInicioPersonalizada) {
          const dInicio = new Date(dataInicioPersonalizada);
          dInicio.setHours(0, 0, 0, 0);
          if (dLimpeza < dInicio) return false;
        }
        if (dataFimPersonalizada) {
          const dFim = new Date(dataFimPersonalizada);
          dFim.setHours(0, 0, 0, 0);
          if (dLimpeza > dFim) return false;
        }
      } else if (filtroMesFinanceiro && item.mesAnoChave !== filtroMesFinanceiro) {
        return false;
      }

      const aptPuro = String(item.apt)
        .replace(/[\s#]+/g, "")
        .toUpperCase();
      if (
        filtroAptsSelecionados.length > 0 &&
        !filtroAptsSelecionados.includes(aptPuro)
      )
        return false;
      return true;
    });
  }, [
    dadosFinanceirosDetalhados,
    filtroMesFinanceiro,
    filtroAptsSelecionados,
    dataInicioPersonalizada,
    dataFimPersonalizada,
  ]);

  // Meses cobertos pelo filtro ativo (mês específico OU período De/Até),
  // usados para calcular a taxa fixa mensal das Áreas Comuns — este custo
  // é por mês inteiro, não por dia, por isso não se filtra como os checkouts.
  const mesesAreaComunsRelevantes = useMemo(() => {
    if (filtroMesFinanceiro) return [filtroMesFinanceiro];
    if (!dataInicioPersonalizada || !dataFimPersonalizada) return [];

    const inicio = new Date(dataInicioPersonalizada);
    const fim = new Date(dataFimPersonalizada);
    const meses = [];
    const cursor = new Date(inicio.getFullYear(), inicio.getMonth(), 1);
    const limite = new Date(fim.getFullYear(), fim.getMonth(), 1);
    while (cursor <= limite) {
      meses.push(
        `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(
          2,
          "0"
        )}`
      );
      cursor.setMonth(cursor.getMonth() + 1);
    }
    return meses;
  }, [filtroMesFinanceiro, dataInicioPersonalizada, dataFimPersonalizada]);

  const areasComunsFaturaveis = useMemo(() => {
    const linhas = [];
    mesesAreaComunsRelevantes.forEach((mes) => {
      Object.keys(configAreasComuns).forEach((aptChave) => {
        const configApt = configAreasComuns[aptChave];
        if (!configApt || !configApt.custoMensal) return;
        if (
          filtroAptsSelecionados.length > 0 &&
          !filtroAptsSelecionados.includes(aptChave.toUpperCase())
        )
          return;
        const [ano, mesNum] = mes.split("-");
        linhas.push({
          idChave: `FIN_AC_${aptChave}_${mes}`,
          apt: `${aptChave}#`,
          mes,
          mesTexto: `${mesNum}/${ano}`,
          valor: configApt.custoMensal,
        });
      });
    });
    return linhas;
  }, [mesesAreaComunsRelevantes, configAreasComuns, filtroAptsSelecionados]);

  const totalAreasComunsPeriodo = useMemo(
    () => areasComunsFaturaveis.reduce((acc, l) => acc + l.valor, 0),
    [areasComunsFaturaveis]
  );

  const dadosFinanceirosOrdenados = useMemo(() => {
    return [...dadosFinanceirosFiltrados].sort((a, b) => {
      let vA, vB;
      if (sortFinColuna === "apt") {
        vA = a.apt.toLowerCase();
        vB = b.apt.toLowerCase();
      } else if (sortFinColuna === "quarto") {
        vA = String(a.quarto);
        vB = String(b.quarto);
      } else if (sortFinColuna === "inquilino") {
        vA = (a.nome || "").toLowerCase();
        vB = (b.nome || "").toLowerCase();
      } else if (sortFinColuna === "total") {
        vA = a.custoCalculado;
        vB = b.custoCalculado;
      } else {
        vA = a.dataFaturaObj ? a.dataFaturaObj.getTime() : 0;
        vB = b.dataFaturaObj ? b.dataFaturaObj.getTime() : 0;
      }
      return vA < vB
        ? sortFinAsc
          ? -1
          : 1
        : vA > vB
        ? sortFinAsc
          ? 1
          : -1
        : 0;
    });
  }, [dadosFinanceirosFiltrados, sortFinColuna, sortFinAsc]);

  const totalCheckoutsPeriodo = useMemo(
    () =>
      dadosFinanceirosFiltrados.reduce(
        (acc, curr) => acc + curr.custoCalculado,
        0
      ),
    [dadosFinanceirosFiltrados]
  );

  const totalFaturadoPeriodo = useMemo(
    () => totalCheckoutsPeriodo + totalAreasComunsPeriodo,
    [totalCheckoutsPeriodo, totalAreasComunsPeriodo]
  );

  const handleExportarExcel = () => {
    const linhasCheckouts =
      dadosFinanceirosOrdenados.length > 0
        ? dadosFinanceirosOrdenados.map((item) => ({
            Apartamento: item.apt,
            Componente: item.quarto,
            "Detalhe / Inquilino": item.nome || "-",
            "Data Fatura":
              item.dataFaturaManual || paraInputDate(item.dataFaturaObj),
            "Serviço Faturado": item.tipoServicoRealizado,
            "Preço Base (€)": item.precoBaseEfetivo,
            "Extras Declarados (€)": item.cobrancaExtraDigitada,
            "Total Linha (€)": item.custoCalculado,
          }))
        : [{ Aviso: "Nenhum checkout encontrado para os filtros aplicados" }];

    const linhasAreasComuns =
      areasComunsFaturaveis.length > 0
        ? areasComunsFaturaveis.map((linha) => ({
            Apartamento: linha.apt,
            Mês: linha.mesTexto,
            "Valor (€)": linha.valor,
          }))
        : [{ Aviso: "Nenhum mês selecionado nos filtros" }];

    const linhasResumo = [
      {
        Descrição: "Total Checkouts",
        "Valor (€)": totalCheckoutsPeriodo,
      },
      { Descrição: "Total Áreas Comuns", "Valor (€)": totalAreasComunsPeriodo },
      { Descrição: "TOTAL GERAL A PAGAR À OLYMPUS", "Valor (€)": totalFaturadoPeriodo },
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(linhasCheckouts),
      "Checkouts"
    );
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(linhasAreasComuns),
      "Areas Comuns"
    );
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(linhasResumo),
      "Resumo"
    );

    const hoje = new Date();
    const carimbo = `${hoje.getFullYear()}${String(
      hoje.getMonth() + 1
    ).padStart(2, "0")}${String(hoje.getDate()).padStart(2, "0")}`;
    XLSX.writeFile(wb, `report_financeiro_olympus_${carimbo}.xlsx`);
  };

  const mesesDisponiveisDropdown = useMemo(() => {
    const lista = new Set();
    dadosFinanceirosDetalhados.forEach((d) => {
      if (d.mesAnoChave) lista.add(d.mesAnoChave);
    });
    return [...lista].sort().reverse();
  }, [dadosFinanceirosDetalhados]);

  const listaAptsUnicosEscalaTotal = useMemo(() => {
    const originais =
      estadias && estadias.length > 0
        ? estadias.map((i) =>
            String(i.apt)
              .replace(/[\s#]+/g, "")
              .toUpperCase()
          )
        : [];
    return [...new Set(originais)].filter(Boolean).sort();
  }, [estadias]);

  // =========================================================
  // 3. HANDLERS E ACÇÕES
  // =========================================================
  const alternarOrdenacao = (coluna, aba) => {
    if (aba === "ativa") {
      if (sortAtivaColuna === coluna) setSortAtivaAsc(!sortAtivaAsc);
      else {
        setSortAtivaColuna(coluna);
        setSortAtivaAsc(true);
      }
    } else if (aba === "report") {
      if (sortReportColuna === coluna) setSortReportAsc(!sortReportAsc);
      else {
        setSortReportColuna(coluna);
        setSortReportAsc(true);
      }
    } else if (aba === "financeiro") {
      if (sortFinColuna === coluna) setSortFinAsc(!sortFinAsc);
      else {
        setSortFinColuna(coluna);
        setSortFinAsc(true);
      }
    }
  };

  const handleToggleSelecaoApt = (apt) => {
    setFiltroAptsSelecionados((prev) =>
      prev.includes(apt) ? prev.filter((a) => a !== apt) : [...prev, apt]
    );
  };

  const handleSubmeterExtraManual = () => {
    if (!extraApt || !extraQuarto || !extraData) {
      alert("Por favor, selecione os dados obrigatórios.");
      return;
    }

    const idGerado = String(Date.now());
    const novaChaveExtra = `EXTRA_${idGerado}`;
    const descFinal =
      extraDescricaoTarefa?.trim() !== ""
        ? extraDescricaoTarefa.trim()
        : extraTipoLimpeza;

    onNotaChange(novaChaveExtra, descFinal);

    if (extraTipoLimpeza) {
      onTipoCheckoutChange(novaChaveExtra, extraTipoLimpeza);
    }

    onAdicionarExtra({
      idCol: idGerado,
      apt: extraApt.trim(),
      quarto: extraQuarto.trim(),
      nome: extraTipoLimpeza || "Preparação Quarto",
      descricao: descFinal,
      dataSaida: extraData,
    });

    setExtraApt("");
    setExtraQuarto("");
    setExtraDescricaoTarefa("");
    setExtraData("");
    setExtraTipoLimpeza("");
    alert("✔️ Limpeza Extra criada!");
  };

  // 🔍 FUNÇÃO ESPERTA: Procura pelos dados em qualquer chave válida (chave nova, id, idCol ou chave antiga)
  const abrirModalReport = (itemOuChave) => {
    if (!itemOuChave) return;

    let idChaveFinal = "";
    let dadosAtuais = null;

    let dataItemParaHistorico = "";

    if (typeof itemOuChave === "object" && itemOuChave !== null) {
      const item = itemOuChave;
      dataItemParaHistorico = paraInputDate(item.dataSaidaEfetivaObj);
      const chaveNova = item.idChave;
      const chaveId = item.id || item.idCol;

      const aptOriginal = String(item.apt || "")
        .toUpperCase()
        .trim();
      const quartoOriginal = String(item.quarto || "")
        .toUpperCase()
        .trim();
      const dataTexto = item.saidaEfetivaTexto || item.dataSaida || "";
      const chaveAntiga = `${aptOriginal}_${quartoOriginal}_${dataTexto}`;

      dadosAtuais =
        reportsOlympus[chaveNova] ||
        reportsOlympus[chaveAntiga] ||
        reportsOlympus[chaveId] ||
        {};

      idChaveFinal =
        (reportsOlympus[chaveNova] && chaveNova) ||
        (reportsOlympus[chaveAntiga] && chaveAntiga) ||
        (reportsOlympus[chaveId] && chaveId) ||
        chaveNova ||
        chaveId;
    } else {
      idChaveFinal = itemOuChave;
      dadosAtuais = reportsOlympus[idChaveFinal] || {};
    }

    setModalItemChave(idChaveFinal);
    setModalItemData(dataItemParaHistorico);
    setModalReportTexto(dadosAtuais.comentario || "");
    setModalFotosAntes(dadosAtuais.fotosAntes || []);
    setModalFotosDepois(dadosAtuais.fotosDepois || []);
    setModalCustoExtra(dadosAtuais.custoExtra || "");
    setModalAberto(true);
  };

  const handleSalvarModalReport = async () => {
    if (!modalItemChave) return;
    const d = reportsOlympus[modalItemChave] || {};
    try {
      await onReportOlympusChange(
        modalItemChave,
        {
          ...d,
          comentario: modalReportTexto,
          fotosAntes: modalFotosAntes,
          fotosDepois: modalFotosDepois,
          custoExtra: modalCustoExtra,
        },
        modalItemData
      );
    } catch (err) {
      console.error(err);
      alert("❌ Erro ao guardar o report. Tenta novamente.");
      return;
    }
    setModalAberto(false);
    setModalItemChave(null);
    alert("✔️ Report Guardado!");
  };

  const handleToggleEstadoLimpo = async (idChave, dataLimpeza) => {
    const d = reportsOlympus[idChave] || {};
    await onReportOlympusChange(
      idChave,
      { ...d, limpo: !d.limpo },
      dataLimpeza
    );
  };

  const abrirLightboxComCarrossel = (listaFotos, indexInicial, titulo = "") => {
    if (!listaFotos || listaFotos.length === 0) return;
    setLightboxListaFotos(listaFotos);
    setLightboxIndexAtivo(indexInicial);
    setLightboxTitulo(titulo);
    setLightboxAberto(true);
  };

  const fecharLightbox = () => {
    setLightboxAberto(false);
    setLightboxListaFotos([]);
    setLightboxIndexAtivo(0);
    setLightboxTitulo("");
  };

  const handleLightboxTouchStart = (e) => {
    touchStartXRef.current = e.touches[0].clientX;
  };

  const handleLightboxTouchEnd = (e) => {
    if (touchStartXRef.current === null) return;
    const deltaX = e.changedTouches[0].clientX - touchStartXRef.current;
    const LIMIAR_SWIPE = 50;
    if (deltaX > LIMIAR_SWIPE) {
      voltarFotoAnterior();
    } else if (deltaX < -LIMIAR_SWIPE) {
      passarProximaFoto();
    }
    touchStartXRef.current = null;
  };

  const passarProximaFoto = () => {
    setLightboxIndexAtivo((prev) =>
      prev === lightboxListaFotos.length - 1 ? 0 : prev + 1
    );
  };

  const voltarFotoAnterior = () => {
    setLightboxIndexAtivo((prev) =>
      prev === 0 ? lightboxListaFotos.length - 1 : prev - 1
    );
  };

  const handleUploadFoto = (e, tipo) => {
    const files = Array.from(e.target.files);
    const chaveItem = modalItemChave || "sem_chave";

    files.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const img = new Image();
        img.src = reader.result;

        img.onload = () => {
          const canvas = document.createElement("canvas");
          const MAX_LARGURA = 1600;
          const MAX_ALTURA = 1600;

          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > MAX_LARGURA) {
              height = Math.round((height * MAX_LARGURA) / width);
              width = MAX_LARGURA;
            }
          } else {
            if (height > MAX_ALTURA) {
              width = Math.round((width * MAX_ALTURA) / height);
              height = MAX_ALTURA;
            }
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, width, height);

          const comp = canvas.toDataURL("image/jpeg", 0.75);

          const nomeFicheiro = `${Date.now()}_${Math.random()
            .toString(36)
            .slice(2, 8)}.jpg`;
          const caminho = `report_olympus/${chaveItem}/${tipo}/${nomeFicheiro}`;
          const fotoRef = ref(storage, caminho);

          uploadString(fotoRef, comp, "data_url")
            .then(() => getDownloadURL(fotoRef))
            .then((url) => {
              if (tipo === "antes") {
                setModalFotosAntes((prev) => [...prev, url]);
              } else {
                setModalFotosDepois((prev) => [...prev, url]);
              }
            })
            .catch((err) => {
              console.error("Erro ao carregar foto para o Storage:", err);
              alert(
                "❌ Não foi possível carregar esta foto. Verifica a tua ligação à internet e tenta novamente."
              );
            });
        };
      };
      reader.readAsDataURL(file);
    });
  };

  const removerFotoAnexo = (index, tipo) => {
    if (tipo === "antes")
      setModalFotosAntes((prev) => prev.filter((_, i) => i !== index));
    else setModalFotosDepois((prev) => prev.filter((_, i) => i !== index));
  };

  const acionarDownloadBlob = (blob, nomeFicheiro) => {
    const urlObjeto = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = urlObjeto;
    a.download = nomeFicheiro;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(urlObjeto);
  };

  const baixarFotoUnica = async (url, nomeFicheiro) => {
    try {
      const resposta = await fetch(url);
      const blob = await resposta.blob();
      acionarDownloadBlob(blob, nomeFicheiro);
    } catch (err) {
      console.error("Erro ao descarregar foto:", err);
      alert("❌ Não foi possível descarregar esta foto.");
    }
  };

  const baixarGrupoFotos = async (listaFotos, nomeBase, rotulo) => {
    if (!listaFotos || listaFotos.length === 0) return;

    const nomeSeguro = nomeBase.replace(/[^a-zA-Z0-9_-]/g, "_");

    try {
      const zip = new JSZip();
      await Promise.all(
        listaFotos.map(async (url, i) => {
          const resposta = await fetch(url);
          const blob = await resposta.blob();
          zip.file(`${rotulo}_${i + 1}.jpg`, blob);
        })
      );
      const zipBlob = await zip.generateAsync({ type: "blob" });
      acionarDownloadBlob(zipBlob, `${nomeSeguro}_${rotulo}.zip`);
    } catch (err) {
      console.error("Erro ao descarregar fotos:", err);
      alert("❌ Não foi possível descarregar as fotos. Verifica a ligação à internet e tenta novamente.");
    }
  };

  const handleMudarPrecoMatriz = (chave, valor) => {
    onPrecoMatrizChange(chave, parseFloat(valor) || 0);
    setMensagemSucesso("✔️ Matriz de Preços atualizada!");
    setTimeout(() => setMensagemSucesso(""), 2000);
  };

  const handleAtualizarDiasAreasComuns = (apt, dia) => {
    const atuais = configAreasComuns[apt].dias;
    const novosDias = atuais.includes(dia)
      ? atuais.filter((d) => d !== dia)
      : [...atuais, dia];
    onConfigAreaComumChange(apt, { ...configAreasComuns[apt], dias: novosDias });
    setMensagemSucesso("✔️ Configuração de Áreas Comuns guardada!");
    setTimeout(() => setMensagemSucesso(""), 2000);
  };

  const handleAtualizarCustoAreasComuns = (apt, valor) => {
    onConfigAreaComumChange(apt, {
      ...configAreasComuns[apt],
      custoMensal: parseFloat(valor) || 0,
    });
    setMensagemSucesso("✔️ Configuração de Áreas Comuns guardada!");
    setTimeout(() => setMensagemSucesso(""), 2000);
  };

  // =========================================================
  // 3.1 CÉLULAS REUTILIZÁVEIS (usadas na tabela desktop E nos cartões mobile)
  // =========================================================
  const renderTipoLimpeza = (estadia, valCheckout) => {
    if (estadia.isAreaComum) {
      const corAC = CORES_TIPO_LIMPEZA["Manutenção"];
      return (
        <span
          style={{
            display: "inline-block",
            padding: "4px 10px",
            borderRadius: "20px",
            fontSize: "11px",
            fontWeight: "bold",
            backgroundColor: corAC.bg,
            color: corAC.cor,
          }}
        >
          Manutenção
        </span>
      );
    }
    if (isAdmin) {
      const corSelect =
        CORES_TIPO_LIMPEZA[valCheckout] || COR_TIPO_LIMPEZA_PADRAO;
      return (
        <select
          value={valCheckout}
          onChange={(e) => onTipoCheckoutChange(estadia.idChave, e.target.value)}
          style={{
            width: "100%",
            padding: "6px",
            borderRadius: "5px",
            border: "1px solid #cbd5e1",
            backgroundColor: corSelect.bg,
            color: corSelect.cor,
            fontWeight: "600",
          }}
        >
          <option value="Checkout Airbnb">Checkout Airbnb</option>
          <option value="Checkout Semestre">Checkout Semestre</option>
          <option value="Preparação Quarto">Preparação Quarto</option>
          <option value="Limpeza Extra">Limpeza Extra</option>
          <option value="Mudança Roupas">Mudança Roupas</option>
          <option value="Manutenção">Manutenção</option>
          <option value="">- Em Branco -</option>
        </select>
      );
    }
    const cor = CORES_TIPO_LIMPEZA[valCheckout] || COR_TIPO_LIMPEZA_PADRAO;
    return (
      <span
        style={{
          display: "inline-block",
          padding: "4px 10px",
          borderRadius: "20px",
          fontSize: "11px",
          fontWeight: "bold",
          backgroundColor: cor.bg,
          color: cor.cor,
        }}
      >
        {valCheckout || "(Em Branco)"}
      </span>
    );
  };

  const renderUrgente = (estadia, eUrgente) => {
    if (estadia.isAreaComum) return "-";
    if (isAdmin) {
      return (
        <button
          onClick={() => onPrioridadeToggle(estadia.idChave)}
          style={{
            background: eUrgente ? "#e11d48" : "#f3f4f6",
            color: eUrgente ? "#fff" : "#9ca3af",
            border: "none",
            borderRadius: "20px",
            padding: "5px 10px",
            fontSize: "11px",
            fontWeight: "bold",
            cursor: "pointer",
          }}
        >
          {eUrgente ? "🚨 URGENTE" : "Normal"}
        </button>
      );
    }
    return (
      <span
        style={{
          display: "inline-block",
          padding: "4px 10px",
          borderRadius: "20px",
          fontSize: "11px",
          fontWeight: "bold",
          backgroundColor: eUrgente ? "#ffe4e6" : "#f3f4f6",
          color: eUrgente ? "#b91c1c" : "#6b7280",
        }}
      >
        {eUrgente ? "🚨 URGENTE" : "Normal"}
      </span>
    );
  };

  const renderNumInquilinos = (estadia) => {
    if (estadia.isAreaComum) return "-";
    if (isAdmin) {
      return (
        <input
          type="number"
          min="1"
          value={
            tempInquilinos[estadia.idChave] !== undefined
              ? tempInquilinos[estadia.idChave]
              : numInquilinos[estadia.idChave] ||
                estadia.inquilinosAutomaticos ||
                ""
          }
          placeholder={
            estadia.inquilinosAutomaticos
              ? String(estadia.inquilinosAutomaticos)
              : "1"
          }
          onChange={(e) => {
            const valor = e.target.value;
            setTempInquilinos((prev) => ({ ...prev, [estadia.idChave]: valor }));
            salvarComDebounce(
              debounceInquilinosRef,
              estadia.idChave,
              valor,
              onNumInquilinosChange
            );
          }}
          style={{
            width: "55px",
            padding: "5px",
            borderRadius: "4px",
            border: "1px solid #cbd5e1",
            textAlign: "center",
            fontWeight:
              !numInquilinos[estadia.idChave] && estadia.inquilinosAutomaticos
                ? "bold"
                : "normal",
          }}
        />
      );
    }
    return (
      numInquilinos[estadia.idChave] || estadia.inquilinosAutomaticos || "1"
    );
  };

  const renderTipoPreparacao = (estadia) => {
    if (estadia.isAreaComum) return "-";
    if (isAdmin) {
      return (
        <select
          value={
            tipoPreparacao[estadia.idChave] || estadia.preparacaoAutomatica || ""
          }
          onChange={(e) =>
            onTipoPreparacaoChange(estadia.idChave, e.target.value)
          }
          style={{
            padding: "5px",
            borderRadius: "4px",
            border: "1px solid #cbd5e1",
            backgroundColor:
              !tipoPreparacao[estadia.idChave] && estadia.preparacaoAutomatica
                ? "#f0f9ff"
                : "#fff",
            fontWeight:
              !tipoPreparacao[estadia.idChave] && estadia.preparacaoAutomatica
                ? "bold"
                : "normal",
          }}
        >
          <option value="">- Selecionar -</option>
          <option value="Airbnb">Airbnb</option>
          <option value="Semestre">Semestre</option>
        </select>
      );
    }
    const valorPrep = tipoPreparacao[estadia.idChave] || estadia.preparacaoAutomatica;
    if (valorPrep) {
      return (
        <span
          style={{
            padding: "4px 8px",
            borderRadius: "12px",
            fontSize: "11px",
            fontWeight: "bold",
            backgroundColor: valorPrep === "Airbnb" ? "#dbeafe" : "#dcfce7",
            color: valorPrep === "Airbnb" ? "#1e40af" : "#15803d",
          }}
        >
          {valorPrep}
        </span>
      );
    }
    return "-";
  };

  const renderNotas = (estadia, valNotas) => {
    if (isAdmin) {
      return (
        <textarea
          value={valNotas}
          onChange={(e) => {
            const valor = e.target.value;
            setTempNotas((prev) => ({ ...prev, [estadia.idChave]: valor }));
            salvarComDebounce(
              debounceNotasRef,
              estadia.idChave,
              valor,
              onNotaChange
            );
          }}
          style={{
            width: "180px",
            minHeight: "45px",
            padding: "6px",
            borderRadius: "5px",
            border: "1px solid #cbd5e1",
            fontSize: "12px",
          }}
        />
      );
    }
    return (
      <span
        style={{
          fontSize: "12px",
          color: "#334155",
          fontWeight: "600",
          display: "block",
          whiteSpace: "pre-line",
          maxWidth: "180px",
          textAlign: "left",
        }}
      >
        {valNotas || "-"}
      </span>
    );
  };

  const renderReportButton = (estadia) => {
    const aptOriginal = String(estadia.apt || "").toUpperCase().trim();
    const quartoOriginal = String(estadia.quarto || "").toUpperCase().trim();
    const dataTexto = estadia.saidaEfetivaTexto || estadia.dataSaida || "";
    const chaveAntiga = `${aptOriginal}_${quartoOriginal}_${dataTexto}`;

    const dataDocRecuperado =
      reportsOlympus[estadia.idChave] ||
      reportsOlympus[chaveAntiga] ||
      reportsOlympus[estadia.id] ||
      reportsOlympus[estadia.idCol] ||
      {};

    const temReport =
      dataDocRecuperado.comentario ||
      (dataDocRecuperado.fotosAntes && dataDocRecuperado.fotosAntes.length > 0) ||
      (dataDocRecuperado.fotosDepois && dataDocRecuperado.fotosDepois.length > 0) ||
      dataDocRecuperado.custoExtra;

    return (
      <button
        onClick={() => abrirModalReport(estadia)}
        style={{
          backgroundColor: "#db2777",
          color: "#fff",
          border: "none",
          borderRadius: "6px",
          padding: "8px 16px",
          fontWeight: "bold",
          cursor: "pointer",
          whiteSpace: "nowrap",
        }}
      >
        {temReport ? "📝 Ver Report" : "➕ Report Olympus"}
      </button>
    );
  };

  const renderAcaoAdmin = (estadia) => {
    const chaveParaOcultar = estadia.idChave || estadia.id || estadia.idCol;
    const estaOculto = limpezasOcultas.includes(chaveParaOcultar);

    return (
      <div
        style={{
          display: "flex",
          gap: "6px",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <button
          onClick={() => handleToggleOcultarLimpeza(chaveParaOcultar)}
          title={estaOculto ? "Mostrar na equipa" : "Ocultar da equipa"}
          style={{
            padding: "5px 8px",
            backgroundColor: estaOculto ? "#94a3b8" : "#64748b",
            color: "#fff",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            fontSize: "12px",
            display: "flex",
            alignItems: "center",
          }}
        >
          {estaOculto ? "🙈 Oculto" : "👁️ Visível"}
        </button>

        {estadia.isExtra ? (
          <button
            onClick={async () => {
              const idParaEliminar = String(estadia.idCol || estadia.id);
              if (!idParaEliminar) {
                alert(
                  "⚠️ Erro: Não foi possível identificar o ID desta limpeza."
                );
                return;
              }
              try {
                await onRemoverExtra(idParaEliminar);
                alert("🗑️ Limpeza Extra eliminada com sucesso!");
              } catch (err) {
                console.error(err);
                alert("❌ Erro ao eliminar a limpeza.");
              }
            }}
            style={{
              padding: "5px 10px",
              backgroundColor: "#e11d48",
              color: "#fff",
              border: "none",
              borderRadius: "4px",
              fontWeight: "bold",
              cursor: "pointer",
            }}
          >
            Eliminar
          </button>
        ) : (
          <span style={{ color: "#94a3b8", fontSize: "11px" }}>
            Automático
          </span>
        )}
      </div>
    );
  };

  return (
    <>
      {/* SELECÇÃO DE SUB-TABS ADMIN */}
      {isAdmin && (
        <div
          style={{
            display: "flex",
            gap: "10px",
            marginBottom: "20px",
            borderBottom: "2px solid #e5e7eb",
          }}
        >
          <button
            onClick={() => setSubAbaLimpezas("ativa")}
            style={{
              padding: "10px 20px",
              border: "none",
              background: "none",
              fontSize: "14px",
              fontWeight: "bold",
              cursor: "pointer",
              borderBottom:
                subAbaLimpezas === "ativa"
                  ? "3px solid #db2777"
                  : "3px solid transparent",
              color: subAbaLimpezas === "ativa" ? "#db2777" : "#6b7280",
            }}
          >
            Resumos da Escala Ativa
          </button>
          <button
            onClick={() => setSubAbaLimpezas("historico")}
            style={{
              padding: "10px 20px",
              border: "none",
              background: "none",
              fontSize: "14px",
              fontWeight: "bold",
              cursor: "pointer",
              borderBottom:
                subAbaLimpezas === "historico"
                  ? "3px solid #db2777"
                  : "3px solid transparent",
              color: subAbaLimpezas === "historico" ? "#db2777" : "#6b7280",
            }}
          >
            📋 Report Limpezas Olympus
          </button>
          <button
            onClick={() => setSubAbaLimpezas("financeiro")}
            style={{
              padding: "10px 20px",
              border: "none",
              background: "none",
              fontSize: "14px",
              fontWeight: "bold",
              cursor: "pointer",
              borderBottom:
                subAbaLimpezas === "financeiro"
                  ? "3px solid #16a34a"
                  : "3px solid transparent",
              color: subAbaLimpezas === "financeiro" ? "#16a34a" : "#6b7280",
            }}
          >
            📊 Report Financeiro Olympus
          </button>
          <button
            onClick={() => setSubAbaLimpezas("gap")}
            style={{
              padding: "10px 20px",
              border: "none",
              background: "none",
              fontSize: "14px",
              fontWeight: "bold",
              cursor: "pointer",
              borderBottom:
                subAbaLimpezas === "gap"
                  ? "3px solid #2563eb"
                  : "3px solid transparent",
              color: subAbaLimpezas === "gap" ? "#2563eb" : "#6b7280",
            }}
          >
            🔵 Estadias GAP Longo
          </button>
        </div>
      )}

      {/* BLOCO DE FILTRO GERAL DE TEXTO DO INQUILINO */}
      <div
        style={{
          background: "#ffffff",
          padding: "12px 18px",
          borderRadius: "10px",
          marginBottom: "15px",
          border: "1px solid #e2e8f0",
          display: "flex",
          alignItems: "center",
          gap: "12px",
        }}
      >
        <span style={{ fontSize: "13px", fontWeight: "700", color: "#475569" }}>
          🔍 Pesquisa por Nome do Inquilino:
        </span>
        <input
          type="text"
          placeholder="Digite o nome do hóspede para filtrar em todas as abas..."
          value={filtroInquilinoNome}
          onChange={(e) => setFiltroInquilinoNome(e.target.value)}
          style={{
            flex: 1,
            padding: "8px 12px",
            borderRadius: "6px",
            border: "1px solid #cbd5e1",
            fontSize: "13px",
          }}
        />
        {filtroInquilinoNome && (
          <button
            onClick={() => setFiltroInquilinoNome("")}
            style={{
              background: "none",
              border: "none",
              color: "#ef4444",
              fontWeight: "700",
              cursor: "pointer",
              fontSize: "13px",
            }}
          >
            Limpar
          </button>
        )}
      </div>

      {/* PAINEL DE DATAS PERSONALIZADAS GLOBAL */}
      <div
        style={{
          background: "#f8fafc",
          border: "1px solid #e2e8f0",
          padding: "12px 18px",
          borderRadius: "10px",
          marginBottom: "20px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: "15px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            flexWrap: "wrap",
          }}
        >
          <span
            style={{ fontSize: "12px", fontWeight: "700", color: "#475569" }}
          >
            📆 Filtrar por Período Personalizado:
          </span>
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <span style={{ fontSize: "12px", color: "#64748b" }}>De:</span>
            <input
              type="date"
              value={dataInicioPersonalizada}
              onChange={(e) => setDataInicioPersonalizada(e.target.value)}
              style={{
                padding: "5px 8px",
                borderRadius: "6px",
                border: "1px solid #cbd5e1",
                fontSize: "12.5px",
              }}
            />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <span style={{ fontSize: "12px", color: "#64748b" }}>Até:</span>
            <input
              type="date"
              value={dataFimPersonalizada}
              onChange={(e) => setDataFimPersonalizada(e.target.value)}
              style={{
                padding: "5px 8px",
                borderRadius: "6px",
                border: "1px solid #cbd5e1",
                fontSize: "12.5px",
              }}
            />
          </div>
        </div>
      </div>

      {/* PAINEL DE CONTROLOS SUPERIORES GLOBAIS */}
      <div
        style={{
          display: "flex",
          gap: "15px",
          marginBottom: "20px",
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <div
          style={{
            background: "#fdf2f8",
            padding: "15px",
            borderRadius: "8px",
            flex: 1,
            minWidth: "200px",
            border: "1px solid #fbcfe8",
          }}
        >
          <h4
            style={{
              margin: "0 0 4px",
              color: "#9d174d",
              fontSize: "13px",
              textTransform: "uppercase",
              fontWeight: "bold",
            }}
          >
            SERVIÇOS FILTRADOS DISPONÍVEIS
          </h4>
          <p
            style={{
              margin: 0,
              fontSize: "28px",
              fontWeight: "bold",
              color: "#db2777",
            }}
          >
            {subAbaLimpezas === "ativa"
              ? dadosOrdenadosAtiva.length
              : subAbaLimpezas === "historico"
              ? dadosOrdenadosReport.length
              : subAbaLimpezas === "gap"
              ? dadosGapLongoOrdenados.length
              : dadosFinanceirosOrdenados.length}
          </p>
        </div>
        <div
          style={{
            background: "#fff",
            padding: "15px",
            borderRadius: "8px",
            flex: 2,
            display: "flex",
            alignItems: "center",
            gap: "10px",
            flexWrap: "wrap",
            justifyContent: "space-between",
            border: "1px solid #cbd5e1",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flexWrap: "wrap",
            }}
          >
            <span
              style={{ fontWeight: "bold", fontSize: "13px", color: "#4b5563" }}
            >
              Período:
            </span>
            <button
              onClick={() => {
                setPeriodoLimpeza("proximos");
                setSortAtivaColuna("data");
                setSortAtivaAsc(true);
                setSortReportColuna("data");
                setSortReportAsc(true);
                setSortFinColuna("data");
                setSortFinAsc(true);
              }}
              style={{
                padding: "8px 12px",
                border: "none",
                borderRadius: "6px",
                fontWeight: "bold",
                cursor: "pointer",
                fontSize: "13px",
                backgroundColor:
                  periodoLimpeza === "proximos" ? "#db2777" : "#f3f4f6",
                color: periodoLimpeza === "proximos" ? "#fff" : "#4b5563",
              }}
            >
              📅 Próximos 3 Dias
            </button>
            <button
              onClick={() => {
                setPeriodoLimpeza("hoje_frente");
                setSortAtivaColuna("data");
                setSortAtivaAsc(true);
                setSortReportColuna("data");
                setSortReportAsc(true);
                setSortFinColuna("data");
                setSortFinAsc(true);
              }}
              style={{
                padding: "8px 12px",
                border: "none",
                borderRadius: "6px",
                fontWeight: "bold",
                cursor: "pointer",
                fontSize: "13px",
                backgroundColor:
                  periodoLimpeza === "hoje_frente" ? "#06b6d4" : "#f3f4f6",
                color: periodoLimpeza === "hoje_frente" ? "#fff" : "#4b5563",
              }}
            >
              🚀 Hoje para a Frente
            </button>
            <button
              onClick={() => setPeriodoLimpeza("todos")}
              style={{
                padding: "8px 12px",
                border: "none",
                borderRadius: "6px",
                fontWeight: "bold",
                cursor: "pointer",
                fontSize: "13px",
                backgroundColor:
                  periodoLimpeza === "todos" ? "#db2777" : "#f3f4f6",
                color: periodoLimpeza === "todos" ? "#fff" : "#4b5563",
              }}
            >
              📋 Ver Todos
            </button>
            <button
              onClick={() => {
                setPeriodoLimpeza("passadas");
                setSortAtivaColuna("data");
                setSortAtivaAsc(false);
                setSortReportColuna("data");
                setSortReportAsc(false);
                setSortFinColuna("data");
                setSortFinAsc(false);
              }}
              style={{
                padding: "8px 12px",
                border: "none",
                borderRadius: "6px",
                fontWeight: "bold",
                cursor: "pointer",
                fontSize: "13px",
                backgroundColor:
                  periodoLimpeza === "passadas" ? "#3b82f6" : "#f3f4f6",
                color: periodoLimpeza === "passadas" ? "#fff" : "#4b5563",
              }}
            >
              ↩️ Limpezas Passadas
            </button>
          </div>
          {subAbaLimpezas === "financeiro" && (
            <div
              style={{
                background: "#f0fdf4",
                border: "1px solid #bbf7d0",
                padding: "6px 14px",
                borderRadius: "6px",
                textAlign: "right",
              }}
            >
              <span
                style={{
                  fontSize: "10px",
                  fontWeight: "700",
                  color: "#16a34a",
                  display: "block",
                }}
              >
                PAGAR TOTAL ACUMULADO À OLYMPUS:
              </span>
              <strong style={{ fontSize: "18px", color: "#16a34a" }}>
                {totalFaturadoPeriodo.toFixed(2)} €
              </strong>
            </div>
          )}
          {subAbaLimpezas === "financeiro" && (
            <button
              onClick={handleExportarExcel}
              style={{
                padding: "8px 16px",
                border: "none",
                borderRadius: "6px",
                fontWeight: "bold",
                cursor: "pointer",
                fontSize: "13px",
                backgroundColor: "#16a34a",
                color: "#fff",
              }}
            >
              📥 Exportar para Excel
            </button>
          )}
          {subAbaLimpezas === "financeiro" && !historicoAntigoCarregado && (
            <button
              onClick={onCarregarReportsAntigos}
              disabled={aCarregarReportsAntigos}
              title="Por defeito só carregamos os últimos ~4 meses de reports, para a app não ficar cada vez mais lenta. Usa isto para reconciliar um período mais antigo."
              style={{
                padding: "8px 16px",
                border: "1px solid #cbd5e1",
                borderRadius: "6px",
                fontWeight: "bold",
                cursor: aCarregarReportsAntigos ? "default" : "pointer",
                fontSize: "13px",
                backgroundColor: "#fff",
                color: "#475569",
                opacity: aCarregarReportsAntigos ? 0.6 : 1,
              }}
            >
              {aCarregarReportsAntigos
                ? "A carregar..."
                : "🔄 Carregar Histórico Mais Antigo"}
            </button>
          )}
        </div>
      </div>

      {/* PAINEL DE FILTRAGEM MULTI-APARTAMENTO POR TAGS */}
      <div
        style={{
          background: "#fff",
          padding: "18px",
          borderRadius: "10px",
          marginBottom: "15px",
          border: "1px solid #cbd5e1",
          display: "flex",
          flexDirection: "column",
          gap: "12px",
        }}
      >
        <div>
          <label
            style={{
              display: "block",
              marginBottom: "6px",
              fontWeight: "700",
              fontSize: "12px",
              color: "#475569",
            }}
          >
            🏢 Seleção Multi-Apartamento:
          </label>
          <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
            {listaAptsUnicosEscalaTotal.map((apt) => {
              const selecionado = filtroAptsSelecionados.includes(apt);
              return (
                <button
                  key={apt}
                  onClick={() => handleToggleSelecaoApt(apt)}
                  style={{
                    padding: "6px 12px",
                    borderRadius: "20px",
                    border: selecionado
                      ? "1px solid #db2777"
                      : "1px solid #cbd5e1",
                    backgroundColor: selecionado ? "#fdf2f8" : "#ffffff",
                    color: selecionado ? "#db2777" : "#475569",
                    fontSize: "12px",
                    fontWeight: "600",
                    cursor: "pointer",
                  }}
                >
                  {apt}#
                </button>
              );
            })}
          </div>
        </div>
        <div style={{ display: "flex", gap: "15px", flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: "150px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "4px",
                fontWeight: "700",
                fontSize: "12px",
                color: "#475569",
              }}
            >
              📅 Filtrar por Mês:
            </label>
            <select
              value={filtroMesAtiva}
              onChange={(e) => setFiltroMesAtiva(e.target.value)}
              style={{
                width: "100%",
                padding: "8px",
                borderRadius: "6px",
                border: "1px solid #cbd5e1",
                fontSize: "13px",
              }}
            >
              <option value="">Todos os Meses</option>
              <option value="01">Janeiro</option>
              <option value="02">Fevereiro</option>
              <option value="03">Março</option>
              <option value="04">Abril</option>
              <option value="05">Maio</option>
              <option value="06">Junho</option>
              <option value="07">Julho</option>
              <option value="08">Agosto</option>
              <option value="09">Setembro</option>
              <option value="10">Outubro</option>
              <option value="11">Novembro</option>
              <option value="12">Dezembro</option>
            </select>
          </div>
          <div style={{ flex: 1, minWidth: "120px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "4px",
                fontWeight: "700",
                fontSize: "12px",
                color: "#475569",
              }}
            >
              🔑 Quarto:
            </label>
            <input
              type="text"
              placeholder="Ex: 3"
              value={filtroQuarto}
              onChange={(e) => setFiltroQuarto(e.target.value)}
              style={{
                width: "100%",
                padding: "7px",
                borderRadius: "6px",
                border: "1px solid #cbd5e1",
                fontSize: "13px",
              }}
            />
          </div>
          <div
            style={{
              flex: 1,
              minWidth: "140px",
              display:
                subAbaLimpezas !== "financeiro" && subAbaLimpezas !== "gap"
                  ? "block"
                  : "none",
            }}
          >
            <label
              style={{
                display: "block",
                marginBottom: "4px",
                fontWeight: "700",
                fontSize: "12px",
                color: "#475569",
              }}
            >
              🧼 Estado Limpeza:
            </label>
            <select
              value={filtroEstadoLimpo}
              onChange={(e) => setFiltroEstadoLimpo(e.target.value)}
              style={{
                width: "100%",
                padding: "8px",
                borderRadius: "6px",
                border: "1px solid #cbd5e1",
                fontSize: "13px",
                fontWeight: "600",
              }}
            >
              <option value="">-- Todos --</option>
              <option value="pendente">⏳ Pendente</option>
              <option value="sim">✅ Limpo</option>
            </select>
          </div>

          <div
            style={{
              flex: 1,
              minWidth: "160px",
              display:
                subAbaLimpezas !== "financeiro" && subAbaLimpezas !== "gap"
                  ? "block"
                  : "none",
            }}
          >
            <label
              style={{
                display: "block",
                marginBottom: "4px",
                fontWeight: "700",
                fontSize: "12px",
                color: "#475569",
              }}
            >
              📋 Tipo de Limpeza:
            </label>
            <select
              value={filtroTipoCheckout}
              onChange={(e) => setFiltroTipoCheckout(e.target.value)}
              style={{
                width: "100%",
                padding: "8px",
                borderRadius: "6px",
                border: "1px solid #cbd5e1",
                fontSize: "13px",
                fontWeight: "600",
              }}
            >
              <option value="">-- Todos --</option>
              <option value="Checkout Airbnb">Checkout Airbnb</option>
              <option value="Checkout Semestre">Checkout Semestre</option>
              <option value="Preparação Quarto">Preparação Quarto</option>
              <option value="Limpeza Extra">Limpeza Extra</option>
              <option value="Mudança Roupas">Mudança Roupas</option>
              <option value="Manutenção">Manutenção</option>
            </select>
          </div>
          <div
            style={{
              flex: 1,
              minWidth: "180px",
              display: subAbaLimpezas === "financeiro" ? "block" : "none",
            }}
          >
            <label
              style={{
                display: "block",
                marginBottom: "4px",
                fontWeight: "700",
                fontSize: "12px",
                color: "#475569",
              }}
            >
              📆 Mês de Fatura / Olympus:
            </label>
            <select
              value={filtroMesFinanceiro}
              onChange={(e) => setFiltroMesFinanceiro(e.target.value)}
              style={{
                width: "100%",
                padding: "8px",
                borderRadius: "6px",
                border: "1px solid #cbd5e1",
                fontSize: "13px",
                fontWeight: "600",
              }}
            >
              <option value="">-- Todos os Meses --</option>
              {mesesDisponiveisDropdown.map((m) => {
                const [ano, mes] = m.split("-");
                return (
                  <option key={m} value={m}>
                    {mes}/{ano}
                  </option>
                );
              })}
            </select>
          </div>
        </div>

        {subAbaLimpezas === "historico" && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              marginTop: "8px",
              padding: "6px 0",
              borderTop: "1px dashed #cbd5e1",
            }}
          >
            <input
              type="checkbox"
              id="checkAvisos"
              checked={apenasComAvisos}
              onChange={(e) => setApenasComAvisos(e.target.checked)}
              style={{ cursor: "pointer", width: "16px", height: "16px" }}
            />
            <label
              htmlFor="checkAvisos"
              style={{
                fontWeight: "700",
                color: "#db2777",
                fontSize: "13px",
                cursor: "pointer",
                userSelect: "none",
              }}
            >
              ⚠️ Mostrar apenas registos que contenham Relatórios ou Fotos da
              Olympus
            </label>
          </div>
        )}
      </div>

      {/* --- SUB-TAB 1: ESCALA ATIVA --- */}
      {subAbaLimpezas === "ativa" && (
        <>
          {isAdmin && (
            <div
              style={{
                display: "flex",
                gap: "12px",
                background: "#fff3f8",
                padding: "16px",
                borderRadius: "10px",
                marginBottom: "20px",
                border: "1px solid #fbcfe8",
                flexWrap: "wrap",
                alignItems: "flex-end",
              }}
            >
              <div style={{ minWidth: "160px", flex: 1 }}>
                <span
                  style={{
                    fontSize: "12px",
                    fontWeight: "800",
                    color: "#9d174d",
                    display: "block",
                    marginBottom: "6px",
                  }}
                >
                  ➕ Criar Limpeza Extra:
                </span>
                <select
                  value={extraApt}
                  onChange={(e) => setExtraApt(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "8px",
                    fontSize: "13px",
                    borderRadius: "5px",
                    border: "1px solid #cbd5e1",
                    backgroundColor: "#fff",
                  }}
                >
                  <option value="">Selecionar Apt.</option>
                  {listaAptsUnicosEscalaTotal.map((apt) => (
                    <option key={apt} value={apt}>
                      {apt}#
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ minWidth: "160px", flex: 1 }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "6px",
                    fontWeight: "800",
                    fontSize: "12px",
                    color: "#9d174d",
                  }}
                >
                  Tipo de Limpeza:
                </label>
                <select
                  value={extraTipoLimpeza}
                  onChange={(e) => setExtraTipoLimpeza(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "8px",
                    fontSize: "13px",
                    borderRadius: "5px",
                    border: "1px solid #cbd5e1",
                    backgroundColor: "#fff",
                    fontWeight: "600",
                  }}
                >
                  <option value="">Selecionar Limpeza</option>
                  <option value="Checkout Airbnb">Checkout Airbnb</option>
                  <option value="Checkout Semestre">Checkout Semestre</option>
                  <option value="Preparação Quarto">Preparação Quarto</option>
                  <option value="Limpeza Extra">Limpeza Extra</option>
                  <option value="Mudança Roupas">Mudança Roupas</option>
                  <option value="Manutenção">Manutenção</option>
                </select>
              </div>

              <div style={{ width: "90px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "6px",
                    fontWeight: "800",
                    fontSize: "12px",
                    color: "#9d174d",
                  }}
                >
                  Quarto:
                </label>
                <input
                  type="text"
                  placeholder="Quarto"
                  value={extraQuarto}
                  onChange={(e) => setExtraQuarto(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "7.5px",
                    fontSize: "13px",
                    borderRadius: "5px",
                    border: "1px solid #cbd5e1",
                    textAlign: "center",
                  }}
                />
              </div>

              <div style={{ minWidth: "220px", flex: 2 }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "6px",
                    fontWeight: "800",
                    fontSize: "12px",
                    color: "#9d174d",
                  }}
                >
                  Instruções:
                </label>
                <input
                  type="text"
                  placeholder="Ex: Mudança de Lençóis"
                  value={extraDescricaoTarefa}
                  onChange={(e) => setExtraDescricaoTarefa(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "7.5px",
                    fontSize: "13px",
                    borderRadius: "5px",
                    border: "1px solid #cbd5e1",
                  }}
                />
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "6px",
                    fontWeight: "800",
                    fontSize: "12px",
                    color: "#9d174d",
                  }}
                >
                  Data:
                </label>
                <input
                  type="date"
                  value={extraData}
                  onChange={(e) => setExtraData(e.target.value)}
                  style={{
                    padding: "6.5px",
                    fontSize: "13px",
                    borderRadius: "5px",
                    border: "1px solid #cbd5e1",
                  }}
                />
              </div>

              <button
                onClick={handleSubmeterExtraManual}
                style={{
                  padding: "8px 16px",
                  backgroundColor: "#db2777",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  fontWeight: "bold",
                  fontSize: "13px",
                  cursor: "pointer",
                }}
              >
                Adicionar À Escala
              </button>
            </div>
          )}

          <div
            className="tabela-desktop"
            style={{
              background: "#fff",
              borderRadius: "8px",
              overflowX: "auto",
              boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
            }}
          >
            <table
              className="tabela-operacional"
              style={{
                width: "100%",
                borderCollapse: "collapse",
                textAlign: "left",
                fontSize: "13px",
              }}
            >
              <thead>
                <tr
                  style={{
                    backgroundColor: "#fdf2f8",
                    borderBottom: "2px solid #fbcfe8",
                  }}
                >
                  <th
                    style={{
                      padding: "14px 16px",
                      fontWeight: "bold",
                      color: "#9d174d",
                      textAlign: "center",
                    }}
                  >
                    Limpo?
                  </th>
                  <th
                    style={{
                      padding: "14px 16px",
                      fontWeight: "bold",
                      color: "#9d174d",
                    }}
                  >
                    Tipo de Limpeza
                  </th>
                  <th
                    style={{
                      padding: "14px 16px",
                      fontWeight: "bold",
                      color: "#9d174d",
                      textAlign: "center",
                    }}
                  >
                    Urgente?
                  </th>
                  <th
                    onClick={() => alternarOrdenacao("apt", "ativa")}
                    style={{
                      padding: "14px 16px",
                      fontWeight: "bold",
                      color: "#9d174d",
                      cursor: "pointer",
                    }}
                  >
                    Apartamento{" "}
                    {sortAtivaColuna === "apt"
                      ? sortAtivaAsc
                        ? "▲"
                        : "▼"
                      : ""}
                  </th>
                  <th
                    onClick={() => alternarOrdenacao("quarto", "ativa")}
                    style={{
                      padding: "14px 16px",
                      fontWeight: "bold",
                      color: "#9d174d",
                      textAlign: "center",
                      cursor: "pointer",
                    }}
                  >
                    Quarto{" "}
                    {sortAtivaColuna === "quarto"
                      ? sortAtivaAsc
                        ? "▲"
                        : "▼"
                      : ""}
                  </th>
                  {isAdmin && (
                    <th
                      onClick={() => alternarOrdenacao("inquilino", "ativa")}
                      style={{
                        padding: "14px 16px",
                        fontWeight: "bold",
                        color: "#475569",
                        cursor: "pointer",
                      }}
                    >
                      👤 Inquilino/Serviço{" "}
                      {sortAtivaColuna === "inquilino"
                        ? sortAtivaAsc
                          ? "▲"
                          : "▼"
                        : ""}
                    </th>
                  )}
                  <th
                    onClick={() => alternarOrdenacao("data", "ativa")}
                    style={{
                      padding: "14px 16px",
                      fontWeight: "bold",
                      color: "#9d174d",
                      cursor: "pointer",
                    }}
                  >
                    Data Limpeza{" "}
                    {sortAtivaColuna === "data"
                      ? sortAtivaAsc
                        ? "▲"
                        : "▼"
                      : ""}
                  </th>
                  <th
                    onClick={() => alternarOrdenacao("inquilinos_num", "ativa")}
                    style={{
                      padding: "14px 16px",
                      fontWeight: "bold",
                      color: "#9d174d",
                      textAlign: "center",
                      cursor: "pointer",
                    }}
                  >
                    Nº Inquilinos{" "}
                    {sortAtivaColuna === "inquilinos_num"
                      ? sortAtivaAsc
                        ? "▲"
                        : "▼"
                      : ""}
                  </th>
                  <th
                    style={{
                      padding: "14px 16px",
                      fontWeight: "bold",
                      color: "#9d174d",
                    }}
                  >
                    Tipo de Quarto
                  </th>
                  <th
                    style={{
                      padding: "14px 16px",
                      fontWeight: "bold",
                      color: "#9d174d",
                    }}
                  >
                    Tipo Preparação
                  </th>
                  {isAdmin && (
                    <th
                      style={{
                        padding: "14px 16px",
                        fontWeight: "bold",
                        color: "#9d174d",
                      }}
                    >
                      🔮 Próximo Checkin
                    </th>
                  )}
                  <th
                    style={{
                      padding: "14px 16px",
                      fontWeight: "bold",
                      color: "#9d174d",
                    }}
                  >
                    📝 Instruções Coolivin
                  </th>
                  <th
                    style={{
                      padding: "14px 16px",
                      fontWeight: "bold",
                      color: "#db2777",
                      textAlign: "center",
                    }}
                  >
                    Report Olympus
                  </th>
                  {isAdmin && (
                    <th
                      style={{
                        padding: "14px 16px",
                        fontWeight: "bold",
                        color: "#9d174d",
                        textAlign: "center",
                      }}
                    >
                      Ação
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {dadosOrdenadosAtiva.map((estadia) => {
                  const chaveUnica =
                    estadia.idChave || estadia.id || estadia.idCol;
                  const estaOculto = limpezasOcultas.includes(chaveUnica);

                  if (!isAdmin && estaOculto) {
                    return null;
                  }

                  const dataDoc = reportsOlympus[estadia.idChave] || {};
                  const isAirbnbOrigem =
                    String(estadia.plataforma).trim().toUpperCase() ===
                    "AIRBNB";
                  const checkoutAutomatico = estadia.isExtra
                    ? ""
                    : isAirbnbOrigem
                    ? "Checkout Airbnb"
                    : "Checkout Semestre";
                  const valCheckout =
                    tipoCheckout[estadia.idChave] || checkoutAutomatico;
                  const valNotas =
                    tempNotas[estadia.idChave] !== undefined
                      ? tempNotas[estadia.idChave]
                      : notasLimpeza[estadia.idChave] ||
                        (estadia.isExtra && estadia.nome !== "Preparação Quarto"
                          ? estadia.nome
                          : "");
                  const chaveInventarioGlobal = `${String(estadia.apt)
                    .toUpperCase()
                    .replace(/[\s#]+/g, "")}_${String(estadia.quarto)
                    .toUpperCase()
                    .replace(/[\s#]+/g, "")}`;
                  const valTipoQuarto =
                    tempQuartos[estadia.idChave] !== undefined
                      ? tempQuartos[estadia.idChave]
                      : tipoQuarto[chaveInventarioGlobal] || "Simples";
                  const eUrgente = !!prioridades[estadia.idChave];

                  return (
                    <tr
                      key={`${estadia.idChave}_${estadia.id ?? estadia.idCol ?? ""}`}
                      style={{
                        borderBottom: "1px solid #fce7f3",
                        backgroundColor: estadia.isAreaComum
                          ? "#f0fdf4"
                          : estadia.ALERTA_GAP_LONGO
                          ? "#eff6ff"
                          : eUrgente
                          ? "#fff1f2"
                          : "transparent",
                      }}
                    >
                      <td
                        data-label="Limpo?"
                        style={{
                          padding: "10px 16px",
                          textAlign: "center",
                          verticalAlign: "middle",
                        }}
                      >
                        <button
                          onClick={() =>
                            handleToggleEstadoLimpo(
                              estadia.idChave,
                              paraInputDate(estadia.dataSaidaEfetivaObj)
                            )
                          }
                          style={{
                            border: "none",
                            borderRadius: "6px",
                            padding: "6px 12px",
                            fontSize: "11.5px",
                            fontWeight: "bold",
                            cursor: "pointer",
                            backgroundColor: dataDoc.limpo
                              ? "#dcfce7"
                              : "#e2e8f0",
                            color: dataDoc.limpo ? "#15803d" : "#475569",
                          }}
                        >
                          {dataDoc.limpo ? "✅ Limpo" : "⏳ Pendente"}
                        </button>
                      </td>
                      <td
                        data-label="Tipo de Limpeza"
                        style={{
                          padding: "10px 16px",
                          verticalAlign: "middle",
                        }}
                      >
                        {renderTipoLimpeza(estadia, valCheckout)}
                      </td>
                      <td
                        data-label="Urgente?"
                        style={{
                          padding: "10px 16px",
                          textAlign: "center",
                          verticalAlign: "middle",
                        }}
                      >
                        {renderUrgente(estadia, eUrgente)}
                      </td>
                      <td
                        data-label="Apartamento"
                        style={{
                          padding: "14px 16px",
                          fontWeight: "bold",
                          verticalAlign: "middle",
                        }}
                      >
                        {estadia.apt}
                      </td>
                      <td
                        data-label="Quarto"
                        style={{
                          padding: "14px 16px",
                          fontWeight: "bold",
                          textAlign: "center",
                          verticalAlign: "middle",
                        }}
                      >
                        {estadia.quarto}
                      </td>
                      {isAdmin && (
                        <td
                          data-label="Inquilino/Serviço"
                          style={{
                            padding: "14px 16px",
                            color: estadia.isAreaComum ? "#16a34a" : "#334155",
                            fontWeight: "600",
                            verticalAlign: "middle",
                          }}
                        >
                          {estadia.isAreaComum
                            ? "🧼 ÁREA COMUM"
                            : estadia.isExtra
                            ? "Limpeza Manual"
                            : estadia.nome || "-"}
                        </td>
                      )}

                      <td
                        data-label="Data Limpeza"
                        style={{
                          padding: "14px 16px",
                          color: "#e11d48",
                          fontWeight: "bold",
                          verticalAlign: "middle",
                        }}
                      >
                        {estadia.saidaEfetivaTexto}
                      </td>

                      <td
                        data-label="Nº Inquilinos"
                        style={{
                          padding: "10px 16px",
                          textAlign: "center",
                          verticalAlign: "middle",
                        }}
                      >
                        {renderNumInquilinos(estadia)}
                      </td>

                      <td
                        data-label="Tipo de Quarto"
                        style={{
                          padding: "10px 16px",
                          verticalAlign: "middle",
                        }}
                      >
                        {estadia.isAreaComum ? "Geral" : valTipoQuarto}
                      </td>

                      <td
                        data-label="Tipo Preparação"
                        style={{
                          padding: "10px 16px",
                          verticalAlign: "middle",
                        }}
                      >
                        {renderTipoPreparacao(estadia)}
                      </td>

                      {isAdmin && (
                        <td
                          data-label="Próximo Checkin"
                          style={{
                            padding: "10px 16px",
                            fontWeight: "600",
                            verticalAlign: "middle",
                            color:
                              estadia.dataCheckinFutura === "Livre"
                                ? "#16a34a"
                                : "#475569",
                          }}
                        >
                          {estadia.dataCheckinFutura}
                        </td>
                      )}

                      <td
                        data-label="Instruções Coolivin"
                        style={{
                          padding: "10px 16px",
                          verticalAlign: "middle",
                        }}
                      >
                        {renderNotas(estadia, valNotas)}
                      </td>

                      {/* 8. COLUNA: REPORT OLYMPUS (MUDANÇA DE TEXTO E BUSCA INTELIGENTE) */}
                      <td
                        data-label="Report Olympus"
                        style={{
                          padding: "10px 16px",
                          textAlign: "center",
                          verticalAlign: "middle",
                        }}
                      >
                        {renderReportButton(estadia)}
                      </td>

                      {isAdmin && (
                        <td
                          data-label="Ação"
                          style={{
                            padding: "10px 16px",
                            textAlign: "center",
                            verticalAlign: "middle",
                          }}
                        >
                          {renderAcaoAdmin(estadia)}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="cartoes-mobile">
            {dadosOrdenadosAtiva.map((estadia) => {
              const chaveUnica =
                estadia.idChave || estadia.id || estadia.idCol;
              const estaOculto = limpezasOcultas.includes(chaveUnica);

              if (!isAdmin && estaOculto) {
                return null;
              }

              const dataDoc = reportsOlympus[estadia.idChave] || {};
              const isAirbnbOrigem =
                String(estadia.plataforma).trim().toUpperCase() === "AIRBNB";
              const checkoutAutomatico = estadia.isExtra
                ? ""
                : isAirbnbOrigem
                ? "Checkout Airbnb"
                : "Checkout Semestre";
              const valCheckout =
                tipoCheckout[estadia.idChave] || checkoutAutomatico;
              const valNotas =
                tempNotas[estadia.idChave] !== undefined
                  ? tempNotas[estadia.idChave]
                  : notasLimpeza[estadia.idChave] ||
                    (estadia.isExtra && estadia.nome !== "Preparação Quarto"
                      ? estadia.nome
                      : "");
              const chaveInventarioGlobal = `${String(estadia.apt)
                .toUpperCase()
                .replace(/[\s#]+/g, "")}_${String(estadia.quarto)
                .toUpperCase()
                .replace(/[\s#]+/g, "")}`;
              const valTipoQuarto =
                tempQuartos[estadia.idChave] !== undefined
                  ? tempQuartos[estadia.idChave]
                  : tipoQuarto[chaveInventarioGlobal] || "Simples";
              const eUrgente = !!prioridades[estadia.idChave];

              const detalhes = [
                ...(isAdmin
                  ? [
                      {
                        label: "Inquilino/Serviço",
                        valor: estadia.isAreaComum
                          ? "🧼 ÁREA COMUM"
                          : estadia.isExtra
                          ? "Limpeza Manual"
                          : estadia.nome || "-",
                      },
                    ]
                  : []),
                { label: "Nº Inquilinos", valor: renderNumInquilinos(estadia) },
                {
                  label: "Tipo de Quarto",
                  valor: estadia.isAreaComum ? "Geral" : valTipoQuarto,
                },
                {
                  label: "Tipo Preparação",
                  valor: renderTipoPreparacao(estadia),
                },
                ...(isAdmin
                  ? [
                      {
                        label: "Próximo Checkin",
                        valor: estadia.dataCheckinFutura,
                      },
                    ]
                  : []),
                {
                  label: "Instruções Coolivin",
                  valor: renderNotas(estadia, valNotas),
                  full: true,
                },
              ];

              return (
                <CardMobile
                  key={`${estadia.idChave}_${estadia.id ?? estadia.idCol ?? ""}`}
                  urgente={eUrgente}
                  alertaGap={estadia.ALERTA_GAP_LONGO}
                  areaComum={estadia.isAreaComum}
                  titulo={
                    estadia.isAreaComum
                      ? `${estadia.apt} · Áreas Comuns`
                      : `${estadia.apt} · Quarto ${estadia.quarto}`
                  }
                  badge={renderUrgente(estadia, eUrgente)}
                  acoes={isAdmin ? renderAcaoAdmin(estadia) : null}
                  resumo={[
                    {
                      label: "Tipo de Limpeza",
                      valor: renderTipoLimpeza(estadia, valCheckout),
                    },
                    { label: "Data Limpeza", valor: estadia.saidaEfetivaTexto },
                    {
                      label: "Limpo?",
                      valor: (
                        <button
                          onClick={() =>
                            handleToggleEstadoLimpo(
                              estadia.idChave,
                              paraInputDate(estadia.dataSaidaEfetivaObj)
                            )
                          }
                          style={{
                            border: "none",
                            borderRadius: "6px",
                            padding: "6px 12px",
                            fontSize: "11.5px",
                            fontWeight: "bold",
                            cursor: "pointer",
                            backgroundColor: dataDoc.limpo
                              ? "#dcfce7"
                              : "#e2e8f0",
                            color: dataDoc.limpo ? "#15803d" : "#475569",
                          }}
                        >
                          {dataDoc.limpo ? "✅ Limpo" : "⏳ Pendente"}
                        </button>
                      ),
                    },
                  ]}
                  detalhes={detalhes}
                  footer={renderReportButton(estadia)}
                />
              );
            })}
          </div>
        </>
      )}

      {/* --- SUB-TAB 2: REPORT LIMPEZAS OLYMPUS --- */}
      {subAbaLimpezas === "historico" && isAdmin && (
        <>
        <div
          className="tabela-desktop"
          style={{
            background: "#fff",
            borderRadius: "8px",
            overflowX: "auto",
            border: "1px solid #e2e8f0",
          }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "13px",
            }}
          >
            <thead>
              <tr
                style={{
                  backgroundColor: "#fdf2f8",
                  borderBottom: "1px solid #e2e8f0",
                }}
              >
                <th
                  style={{
                    padding: "12px 14px",
                    textAlign: "center",
                    width: "140px",
                  }}
                >
                  Limpo?
                </th>
                <th
                  onClick={() => alternarOrdenacao("apt", "report")}
                  style={{
                    padding: "12px 14px",
                    fontWeight: "700",
                    cursor: "pointer",
                  }}
                >
                  Apartamento{" "}
                  {sortReportColuna === "apt"
                    ? sortReportAsc
                      ? "▲"
                      : "▼"
                    : ""}
                </th>
                <th
                  onClick={() => alternarOrdenacao("quarto", "report")}
                  style={{
                    padding: "12px 14px",
                    fontWeight: "600",
                    textAlign: "center",
                    cursor: "pointer",
                  }}
                >
                  Quarto{" "}
                  {sortReportColuna === "quarto"
                    ? sortReportAsc
                      ? "▲"
                      : "▼"
                    : ""}
                </th>
                <th
                  onClick={() => alternarOrdenacao("inquilino", "report")}
                  style={{
                    padding: "12px 14px",
                    color: "#475569",
                    fontWeight: "700",
                    cursor: "pointer",
                  }}
                >
                  👤 Inquilino/Serviço{" "}
                  {sortReportColuna === "inquilino"
                    ? sortReportAsc
                      ? "▲"
                      : "▼"
                    : ""}
                </th>
                <th
                  onClick={() => alternarOrdenacao("data", "report")}
                  style={{
                    padding: "12px 14px",
                    color: "#e11d48",
                    fontWeight: "bold",
                    textAlign: "center",
                    cursor: "pointer",
                  }}
                >
                  Data Limpeza{" "}
                  {sortReportColuna === "data"
                    ? sortReportAsc
                      ? "▲"
                      : "▼"
                    : ""}
                </th>
                <th style={{ padding: "12px 14px", textAlign: "left" }}>
                  🚨 Relatório e Fotos da Olympus
                </th>
                <th style={{ padding: "12px 14px", textAlign: "left" }}>
                  🔒 Observações Internas
                </th>
              </tr>
            </thead>
            <tbody>
              {dadosOrdenadosReport.map((limpeza, indiceReport) => {
                const dataDoc = reportsOlympus[limpeza.idChave] || {};
                return (
                  <tr
                    key={`${limpeza.idChave}_${limpeza.id ?? limpeza.idCol ?? ""}`}
                    style={{
                      borderBottom: "3px solid #fbcfe8",
                      backgroundColor: limpeza.isAreaComum
                        ? "#f0fdf4"
                        : indiceReport % 2 === 0
                        ? "#ffffff"
                        : "#fdf2f8",
                    }}
                  >
                    <td
                      style={{
                        padding: "14px 14px",
                        textAlign: "center",
                        verticalAlign: "top",
                      }}
                    >
                      <span
                        style={{
                          padding: "4px 8px",
                          borderRadius: "4px",
                          fontSize: "11px",
                          fontWeight: "bold",
                          backgroundColor: dataDoc.limpo
                            ? "#dcfce7"
                            : "#fee2e2",
                          color: dataDoc.limpo ? "#15803d" : "#b91c1c",
                        }}
                      >
                        {dataDoc.limpo ? "✅ Limpo" : "⏳ Pendente"}
                      </span>
                    </td>
                    <td
                      style={{
                        padding: "14px 14px",
                        fontWeight: "700",
                        verticalAlign: "top",
                      }}
                    >
                      {limpeza.apt}
                    </td>
                    <td
                      style={{
                        padding: "14px 14px",
                        fontWeight: "600",
                        textAlign: "center",
                        verticalAlign: "top",
                      }}
                    >
                      {limpeza.quarto}
                    </td>
                    <td
                      style={{
                        padding: "14px 14px",
                        color: limpeza.isAreaComum ? "#16a34a" : "#334155",
                        fontWeight: "600",
                        verticalAlign: "top",
                      }}
                    >
                      {limpeza.isAreaComum
                        ? "🧼 ÁREA COMUM"
                        : limpeza.isExtra
                        ? "Limpeza Manual"
                        : limpeza.nome || "-"}
                    </td>
                    <td
                      style={{
                        padding: "14px 14px",
                        color: "#e11d48",
                        fontWeight: "bold",
                        textAlign: "center",
                        verticalAlign: "top",
                      }}
                    >
                      {limpeza.saidaEfetivaTexto}
                    </td>
                    <td
                      style={{
                        padding: "14px 14px",
                        textAlign: "left",
                        verticalAlign: "top",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "10px",
                        }}
                      >
                        {dataDoc.comentario && (
                          <div
                            style={{
                              padding: "8px 12px",
                              backgroundColor: "#f1f5f9",
                              borderRadius: "6px",
                            }}
                          >
                            <strong>Comentário:</strong> {dataDoc.comentario}
                          </div>
                        )}

                        {dataDoc.fotosAntes &&
                          dataDoc.fotosAntes.length > 0 && (
                            <div>
                              <span
                                style={{
                                  fontSize: "11px",
                                  fontWeight: "bold",
                                  color: "#64748b",
                                  display: "block",
                                  marginBottom: "4px",
                                }}
                              >
                                📸 Antes ({dataDoc.fotosAntes.length}):
                              </span>
                              <div
                                style={{
                                  display: "flex",
                                  gap: "6px",
                                  flexWrap: "wrap",
                                }}
                              >
                                {dataDoc.fotosAntes.map((img, i) => (
                                  <img
                                    key={`antes_${i}`}
                                    src={img}
                                    alt="Antes"
                                    onClick={() =>
                                      abrirLightboxComCarrossel(
                                        dataDoc.fotosAntes,
                                        i,
                                        "📸 Antes"
                                      )
                                    }
                                    style={{
                                      width: "50px",
                                      height: "50px",
                                      objectFit: "cover",
                                      borderRadius: "6px",
                                      cursor: "pointer",
                                      border: "1.5px solid #cbd5e1",
                                    }}
                                  />
                                ))}
                              </div>
                            </div>
                          )}

                        {dataDoc.fotosDepois &&
                          dataDoc.fotosDepois.length > 0 && (
                            <div>
                              <span
                                style={{
                                  fontSize: "11px",
                                  fontWeight: "bold",
                                  color: "#16a34a",
                                  display: "block",
                                  marginBottom: "4px",
                                }}
                              >
                                ✨ Depois ({dataDoc.fotosDepois.length}):
                              </span>
                              <div
                                style={{
                                  display: "flex",
                                  gap: "6px",
                                  flexWrap: "wrap",
                                }}
                              >
                                {dataDoc.fotosDepois.map((img, i) => (
                                  <img
                                    key={`depois_${i}`}
                                    src={img}
                                    alt="Depois"
                                    onClick={() =>
                                      abrirLightboxComCarrossel(
                                        dataDoc.fotosDepois,
                                        i,
                                        "✨ Depois"
                                      )
                                    }
                                    style={{
                                      width: "50px",
                                      height: "50px",
                                      objectFit: "cover",
                                      borderRadius: "6px",
                                      cursor: "pointer",
                                      border: "1.5px solid #86efac",
                                    }}
                                  />
                                ))}
                              </div>
                            </div>
                          )}

                        {((dataDoc.fotosAntes && dataDoc.fotosAntes.length > 0) ||
                          (dataDoc.fotosDepois &&
                            dataDoc.fotosDepois.length > 0)) && (
                          <div style={{ display: "flex", gap: "6px" }}>
                            {dataDoc.fotosAntes &&
                              dataDoc.fotosAntes.length > 0 && (
                                <button
                                  onClick={() =>
                                    baixarGrupoFotos(
                                      dataDoc.fotosAntes,
                                      `${limpeza.apt}_${limpeza.quarto}`,
                                      "antes"
                                    )
                                  }
                                  style={{
                                    padding: "5px 10px",
                                    borderRadius: "6px",
                                    border: "1px solid #cbd5e1",
                                    backgroundColor: "#ffffff",
                                    color: "#334155",
                                    fontSize: "11px",
                                    fontWeight: "bold",
                                    cursor: "pointer",
                                  }}
                                >
                                  ⬇️ Descarregar Antes
                                </button>
                              )}
                            {dataDoc.fotosDepois &&
                              dataDoc.fotosDepois.length > 0 && (
                                <button
                                  onClick={() =>
                                    baixarGrupoFotos(
                                      dataDoc.fotosDepois,
                                      `${limpeza.apt}_${limpeza.quarto}`,
                                      "depois"
                                    )
                                  }
                                  style={{
                                    padding: "5px 10px",
                                    borderRadius: "6px",
                                    border: "1px solid #cbd5e1",
                                    backgroundColor: "#ffffff",
                                    color: "#334155",
                                    fontSize: "11px",
                                    fontWeight: "bold",
                                    cursor: "pointer",
                                  }}
                                >
                                  ⬇️ Descarregar Depois
                                </button>
                              )}
                          </div>
                        )}
                      </div>
                    </td>
                    <td
                      style={{
                        padding: "14px 14px",
                        textAlign: "left",
                        verticalAlign: "top",
                      }}
                    >
                      <textarea
                        value={
                          tempObsInterna[limpeza.idChave] !== undefined
                            ? tempObsInterna[limpeza.idChave]
                            : dataDoc.observacaoInterna || ""
                        }
                        onChange={(e) => {
                          const valor = e.target.value;
                          setTempObsInterna((prev) => ({
                            ...prev,
                            [limpeza.idChave]: valor,
                          }));
                          salvarComDebounce(
                            debounceObsInternaRef,
                            limpeza.idChave,
                            valor,
                            onObservacaoInternaChange
                          );
                        }}
                        placeholder="Só visível para a nossa equipa..."
                        style={{
                          width: "180px",
                          minHeight: "50px",
                          padding: "6px",
                          borderRadius: "5px",
                          border: "1px solid #cbd5e1",
                          backgroundColor: "#fffbeb",
                          fontSize: "12px",
                        }}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>

          <div className="cartoes-mobile">
            {dadosOrdenadosReport.map((limpeza) => {
              const dataDoc = reportsOlympus[limpeza.idChave] || {};
              const temFotosOuComentario =
                dataDoc.comentario ||
                (dataDoc.fotosAntes && dataDoc.fotosAntes.length > 0) ||
                (dataDoc.fotosDepois && dataDoc.fotosDepois.length > 0);

              return (
                <CardMobile
                  key={`${limpeza.idChave}_${limpeza.id ?? limpeza.idCol ?? ""}`}
                  titulo={`${limpeza.apt} · ${limpeza.quarto}`}
                  badge={
                    <span
                      style={{
                        padding: "4px 8px",
                        borderRadius: "4px",
                        fontSize: "11px",
                        fontWeight: "bold",
                        backgroundColor: dataDoc.limpo ? "#dcfce7" : "#fee2e2",
                        color: dataDoc.limpo ? "#15803d" : "#b91c1c",
                      }}
                    >
                      {dataDoc.limpo ? "✅ Limpo" : "⏳ Pendente"}
                    </span>
                  }
                  resumo={[
                    { label: "Data Limpeza", valor: limpeza.saidaEfetivaTexto },
                    {
                      label: "Inquilino/Serviço",
                      valor: limpeza.isAreaComum
                        ? "🧼 ÁREA COMUM"
                        : limpeza.isExtra
                        ? "Limpeza Manual"
                        : limpeza.nome || "-",
                    },
                  ]}
                  detalhes={[
                    ...(temFotosOuComentario
                      ? [
                          {
                            label: "Relatório e Fotos",
                            full: true,
                            valor: (
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: "10px",
                                }}
                              >
                                {dataDoc.comentario && (
                                  <div
                                    style={{
                                      padding: "8px 12px",
                                      backgroundColor: "#f1f5f9",
                                      borderRadius: "6px",
                                    }}
                                  >
                                    <strong>Comentário:</strong>{" "}
                                    {dataDoc.comentario}
                                  </div>
                                )}
                                {dataDoc.fotosAntes &&
                                  dataDoc.fotosAntes.length > 0 && (
                                    <div>
                                      <span
                                        style={{
                                          fontSize: "11px",
                                          fontWeight: "bold",
                                          color: "#64748b",
                                          display: "block",
                                          marginBottom: "4px",
                                        }}
                                      >
                                        📸 Antes ({dataDoc.fotosAntes.length}):
                                      </span>
                                      <div
                                        style={{
                                          display: "flex",
                                          gap: "6px",
                                          flexWrap: "wrap",
                                        }}
                                      >
                                        {dataDoc.fotosAntes.map((img, i) => (
                                          <img
                                            key={`antes_${i}`}
                                            src={img}
                                            alt="Antes"
                                            onClick={() =>
                                              abrirLightboxComCarrossel(
                                                dataDoc.fotosAntes,
                                                i,
                                                "📸 Antes"
                                              )
                                            }
                                            style={{
                                              width: "50px",
                                              height: "50px",
                                              objectFit: "cover",
                                              borderRadius: "6px",
                                              cursor: "pointer",
                                              border: "1.5px solid #cbd5e1",
                                            }}
                                          />
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                {dataDoc.fotosDepois &&
                                  dataDoc.fotosDepois.length > 0 && (
                                    <div>
                                      <span
                                        style={{
                                          fontSize: "11px",
                                          fontWeight: "bold",
                                          color: "#16a34a",
                                          display: "block",
                                          marginBottom: "4px",
                                        }}
                                      >
                                        ✨ Depois ({dataDoc.fotosDepois.length}
                                        ):
                                      </span>
                                      <div
                                        style={{
                                          display: "flex",
                                          gap: "6px",
                                          flexWrap: "wrap",
                                        }}
                                      >
                                        {dataDoc.fotosDepois.map((img, i) => (
                                          <img
                                            key={`depois_${i}`}
                                            src={img}
                                            alt="Depois"
                                            onClick={() =>
                                              abrirLightboxComCarrossel(
                                                dataDoc.fotosDepois,
                                                i,
                                                "✨ Depois"
                                              )
                                            }
                                            style={{
                                              width: "50px",
                                              height: "50px",
                                              objectFit: "cover",
                                              borderRadius: "6px",
                                              cursor: "pointer",
                                              border: "1.5px solid #86efac",
                                            }}
                                          />
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                {((dataDoc.fotosAntes &&
                                  dataDoc.fotosAntes.length > 0) ||
                                  (dataDoc.fotosDepois &&
                                    dataDoc.fotosDepois.length > 0)) && (
                                  <div style={{ display: "flex", gap: "6px" }}>
                                    {dataDoc.fotosAntes &&
                                      dataDoc.fotosAntes.length > 0 && (
                                        <button
                                          onClick={() =>
                                            baixarGrupoFotos(
                                              dataDoc.fotosAntes,
                                              `${limpeza.apt}_${limpeza.quarto}`,
                                              "antes"
                                            )
                                          }
                                          style={{
                                            padding: "5px 10px",
                                            borderRadius: "6px",
                                            border: "1px solid #cbd5e1",
                                            backgroundColor: "#ffffff",
                                            color: "#334155",
                                            fontSize: "11px",
                                            fontWeight: "bold",
                                            cursor: "pointer",
                                          }}
                                        >
                                          ⬇️ Descarregar Antes
                                        </button>
                                      )}
                                    {dataDoc.fotosDepois &&
                                      dataDoc.fotosDepois.length > 0 && (
                                        <button
                                          onClick={() =>
                                            baixarGrupoFotos(
                                              dataDoc.fotosDepois,
                                              `${limpeza.apt}_${limpeza.quarto}`,
                                              "depois"
                                            )
                                          }
                                          style={{
                                            padding: "5px 10px",
                                            borderRadius: "6px",
                                            border: "1px solid #cbd5e1",
                                            backgroundColor: "#ffffff",
                                            color: "#334155",
                                            fontSize: "11px",
                                            fontWeight: "bold",
                                            cursor: "pointer",
                                          }}
                                        >
                                          ⬇️ Descarregar Depois
                                        </button>
                                      )}
                                  </div>
                                )}
                              </div>
                            ),
                          },
                        ]
                      : []),
                    {
                      label: "Observações Internas",
                      full: true,
                      valor: (
                        <div>
                          <span
                            style={{
                              fontSize: "11px",
                              fontWeight: "bold",
                              color: "#92400e",
                              display: "block",
                              marginBottom: "4px",
                            }}
                          >
                            🔒 Observações Internas (só a nossa equipa vê):
                          </span>
                          <textarea
                            value={
                              tempObsInterna[limpeza.idChave] !== undefined
                                ? tempObsInterna[limpeza.idChave]
                                : dataDoc.observacaoInterna || ""
                            }
                            onChange={(e) => {
                              const valor = e.target.value;
                              setTempObsInterna((prev) => ({
                                ...prev,
                                [limpeza.idChave]: valor,
                              }));
                              salvarComDebounce(
                                debounceObsInternaRef,
                                limpeza.idChave,
                                valor,
                                onObservacaoInternaChange
                              );
                            }}
                            placeholder="Só visível para a nossa equipa..."
                            style={{
                              width: "100%",
                              minHeight: "50px",
                              padding: "6px",
                              borderRadius: "5px",
                              border: "1px solid #cbd5e1",
                              backgroundColor: "#fffbeb",
                              fontSize: "12px",
                            }}
                          />
                        </div>
                      ),
                    },
                  ]}
                />
              );
            })}
          </div>
        </>
      )}

      {/* --- SUB-TAB 3: REPORT FINANCEIRO OLYMPUS --- */}
      {subAbaLimpezas === "financeiro" && isAdmin && (
        <>
          <div
            style={{
              background: "#ffffff",
              padding: "20px",
              borderRadius: "12px",
              border: "1px solid #e2e8f0",
              marginBottom: "24px",
            }}
          >
            <h3
              style={{
                margin: "0 0 16px 0",
                fontSize: "14px",
                fontWeight: "700",
                color: "#0f172a",
                textTransform: "uppercase",
              }}
            >
              🛠️ Tabela de Preços Fixos Acordados à Olympus
            </h3>
            <div
              style={{
                display: "grid",
                gap: "15px",
                gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
              }}
            >
              {Object.keys(precosMatriz).map((chave) => {
                const [quartoLabel, checkoutLabel] = chave.split("_");
                return (
                  <div
                    key={chave}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "4px",
                      background: "#f8fafc",
                      padding: "10px",
                      borderRadius: "6px",
                      border: "1px solid #e2e8f0",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "12px",
                        fontWeight: "600",
                        color: "#475569",
                      }}
                    >
                      {quartoLabel} + {checkoutLabel}
                    </span>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <input
                        type="number"
                        value={precosMatriz[chave]}
                        onChange={(e) =>
                          handleMudarPrecoMatriz(chave, e.target.value)
                        }
                        style={{
                          width: "100%",
                          padding: "6px",
                          fontSize: "13px",
                          borderRadius: "4px",
                          border: "1px solid #cbd5e1",
                          textAlign: "right",
                          fontWeight: "700",
                        }}
                      />
                      <span
                        style={{
                          fontSize: "14px",
                          fontWeight: "700",
                          color: "#64748b",
                        }}
                      >
                        €
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div
            style={{
              background: "#ffffff",
              padding: "20px",
              borderRadius: "12px",
              border: "1px solid #bbf7d0",
              marginBottom: "24px",
            }}
          >
            <h3
              style={{
                margin: "0 0 14px 0",
                fontSize: "14px",
                fontWeight: "800",
                color: "#16a34a",
                textTransform: "uppercase",
              }}
            >
              🧼 Configuração e Custo Mensal das Áreas Comuns
            </h3>
            <div
              style={{
                display: "grid",
                gap: "20px",
                gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
              }}
            >
              {Object.keys(configAreasComuns).map((aptKey) => {
                const itemConfig = configAreasComuns[aptKey];
                const diasSemana = [
                  "Segunda",
                  "Terça",
                  "Quarta",
                  "Quinta",
                  "Sexta",
                ];
                return (
                  <div
                    key={aptKey}
                    style={{
                      background: "#f0fdf4",
                      padding: "14px",
                      borderRadius: "8px",
                      border: "1px solid #dcfce7",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: "8px",
                        alignItems: "center",
                      }}
                    >
                      <strong style={{ fontSize: "14px", color: "#14532d" }}>
                        Apartamento {aptKey}#
                      </strong>
                      <input
                        type="number"
                        value={itemConfig.custoMensal}
                        onChange={(e) =>
                          handleAtualizarCustoAreasComuns(
                            aptKey,
                            e.target.value
                          )
                        }
                        style={{
                          width: "80px",
                          padding: "4px",
                          fontSize: "13px",
                          textAlign: "right",
                          fontWeight: "700",
                          border: "1px solid #bbf7d0",
                          borderRadius: "4px",
                        }}
                      />
                      <span
                        style={{
                          fontSize: "13px",
                          fontWeight: "700",
                          color: "#16a34a",
                        }}
                      >
                        €/mês
                      </span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        gap: "4px",
                        flexWrap: "wrap",
                        marginTop: "6px",
                      }}
                    >
                      {diasSemana.map((dia) => {
                        const ativo = itemConfig.dias.includes(dia);
                        return (
                          <button
                            key={dia}
                            onClick={() =>
                              handleAtualizarDiasAreasComuns(aptKey, dia)
                            }
                            style={{
                              padding: "4px 8px",
                              fontSize: "11px",
                              borderRadius: "4px",
                              border: ativo
                                ? "1px solid #16a34a"
                                : "1px solid #cbd5e1",
                              backgroundColor: ativo ? "#16a34a" : "#fff",
                              color: ativo ? "#fff" : "#475569",
                              cursor: "pointer",
                            }}
                          >
                            {dia.substring(0, 3)}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div
            style={{
              background: "#ffffff",
              padding: "20px",
              borderRadius: "12px",
              border: "1px solid #e2e8f0",
              marginBottom: "24px",
            }}
          >
            <h3
              style={{
                margin: "0 0 4px 0",
                fontSize: "14px",
                fontWeight: "800",
                color: "#16a34a",
                textTransform: "uppercase",
              }}
            >
              🧼 Áreas Comuns a Faturar neste Período
            </h3>
            <p style={{ margin: "0 0 16px 0", fontSize: "12px", color: "#64748b" }}>
              Custo fixo mensal — escolhe um "Mês de Fatura" ou um período De/Até
              acima para veres aqui os meses correspondentes.
            </p>
            {areasComunsFaturaveis.length === 0 ? (
              <p style={{ fontSize: "13px", color: "#94a3b8", margin: 0 }}>
                Nenhum mês selecionado nos filtros acima.
              </p>
            ) : (
              <>
                <div
                  style={{
                    display: "grid",
                    gap: "10px",
                    gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
                    marginBottom: "14px",
                  }}
                >
                  {areasComunsFaturaveis.map((linha) => (
                    <div
                      key={linha.idChave}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        background: "#f0fdf4",
                        border: "1px solid #dcfce7",
                        borderRadius: "6px",
                        padding: "8px 12px",
                      }}
                    >
                      <span style={{ fontSize: "13px", fontWeight: "700", color: "#14532d" }}>
                        {linha.apt} · {linha.mesTexto}
                      </span>
                      <span style={{ fontSize: "13px", fontWeight: "700", color: "#16a34a" }}>
                        {linha.valor.toFixed(2)} €
                      </span>
                    </div>
                  ))}
                </div>
                <div style={{ textAlign: "right", fontSize: "13px", color: "#16a34a" }}>
                  <strong>Subtotal Áreas Comuns: {totalAreasComunsPeriodo.toFixed(2)} €</strong>
                </div>
              </>
            )}
          </div>

          <div
            style={{
              background: "#f0fdf4",
              border: "1px solid #bbf7d0",
              padding: "12px 20px",
              borderRadius: "8px",
              marginBottom: "16px",
              textAlign: "right",
            }}
          >
            <strong style={{ fontSize: "15px", color: "#16a34a" }}>
              Subtotal Limpezas (sem Áreas Comuns): {totalCheckoutsPeriodo.toFixed(2)} €
            </strong>
          </div>

          <div
            className="tabela-desktop"
            style={{
              background: "#fff",
              borderRadius: "8px",
              overflowX: "auto",
              border: "1px solid #e2e8f0",
            }}
          >
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: "13px",
                textAlign: "left",
              }}
            >
              <thead>
                <tr
                  style={{
                    backgroundColor: "#f0fdf4",
                    borderBottom: "2px solid #bbf7d0",
                  }}
                >
                  <th
                    onClick={() => alternarOrdenacao("apt", "financeiro")}
                    style={{
                      padding: "12px 14px",
                      color: "#16a34a",
                      fontWeight: "700",
                      cursor: "pointer",
                    }}
                  >
                    Apartamento{" "}
                    {sortFinColuna === "apt" ? (sortFinAsc ? "▲" : "▼") : ""}
                  </th>
                  <th
                    onClick={() => alternarOrdenacao("quarto", "financeiro")}
                    style={{
                      padding: "12px 14px",
                      color: "#16a34a",
                      fontWeight: "700",
                      textAlign: "center",
                      cursor: "pointer",
                    }}
                  >
                    Componente{" "}
                    {sortFinColuna === "quarto" ? (sortFinAsc ? "▲" : "▼") : ""}
                  </th>
                  <th
                    onClick={() => alternarOrdenacao("inquilino", "financeiro")}
                    style={{
                      padding: "12px 14px",
                      color: "#16a34a",
                      fontWeight: "700",
                      cursor: "pointer",
                    }}
                  >
                    👤 Detalhe / Inquilino{" "}
                    {sortFinColuna === "inquilino"
                      ? sortFinAsc
                        ? "▲"
                        : "▼"
                      : ""}
                  </th>
                  <th
                    onClick={() => alternarOrdenacao("data", "financeiro")}
                    style={{
                      padding: "12px 14px",
                      color: "#16a34a",
                      fontWeight: "700",
                      cursor: "pointer",
                    }}
                  >
                    Data Fatura{" "}
                    {sortFinColuna === "data" ? (sortFinAsc ? "▲" : "▼") : ""}
                  </th>
                  <th
                    style={{
                      padding: "12px 14px",
                      color: "#16a34a",
                      fontWeight: "700",
                    }}
                  >
                    Serviço Faturado
                  </th>
                  <th
                    style={{
                      padding: "12px 14px",
                      color: "#16a34a",
                      fontWeight: "700",
                      textAlign: "right",
                    }}
                  >
                    Preço Base
                  </th>
                  <th
                    style={{
                      padding: "12px 14px",
                      color: "#b45309",
                      fontWeight: "700",
                      textAlign: "right",
                    }}
                  >
                    Extras Declarados
                  </th>
                  <th
                    onClick={() => alternarOrdenacao("total", "financeiro")}
                    style={{
                      padding: "12px 14px",
                      color: "#16a34a",
                      fontWeight: "700",
                      textAlign: "right",
                      cursor: "pointer",
                    }}
                  >
                    Total Linha{" "}
                    {sortFinColuna === "total" ? (sortFinAsc ? "▲" : "▼") : ""}
                  </th>
                </tr>
              </thead>
              <tbody>
                {dadosFinanceirosOrdenados.map((item) => (
                  <tr
                    key={`${item.idChave}_${item.id ?? item.idCol ?? ""}`}
                    style={{
                      borderBottom: "1px solid #f0fdf4",
                      backgroundColor: item.isAreaComum
                        ? "#f0fdf4"
                        : "transparent",
                    }}
                  >
                    <td
                      style={{
                        padding: "12px 14px",
                        fontWeight: "700",
                        verticalAlign: "middle",
                      }}
                    >
                      {item.apt}
                    </td>
                    <td
                      style={{
                        padding: "12px 14px",
                        textAlign: "center",
                        fontWeight: "600",
                        verticalAlign: "middle",
                      }}
                    >
                      {item.quarto}
                    </td>
                    <td
                      style={{
                        padding: "12px 14px",
                        color: item.isAreaComum ? "#16a34a" : "#334155",
                        fontWeight: "500",
                        verticalAlign: "middle",
                      }}
                    >
                      {item.isAreaComum
                        ? "Ajuste Contratual Fixado"
                        : item.nome || "-"}
                    </td>
                    <td
                      style={{
                        padding: "12px 14px",
                        color: "#475569",
                        verticalAlign: "middle",
                      }}
                    >
                      <input
                        type="date"
                        value={
                          tempDataFatura[item.idChave] !== undefined
                            ? tempDataFatura[item.idChave]
                            : item.dataFaturaManual ||
                              paraInputDate(item.dataFaturaObj)
                        }
                        onChange={(e) => {
                          const valor = e.target.value;
                          setTempDataFatura((prev) => ({
                            ...prev,
                            [item.idChave]: valor,
                          }));
                          salvarComDebounce(
                            debounceDataFaturaRef,
                            item.idChave,
                            valor,
                            onDataFaturaChange
                          );
                        }}
                        style={{
                          padding: "5px",
                          fontSize: "12px",
                          borderRadius: "4px",
                          border: "1px solid #cbd5e1",
                        }}
                      />
                    </td>
                    <td
                      style={{ padding: "12px 14px", verticalAlign: "middle" }}
                    >
                      <span
                        style={{
                          padding: "3px 8px",
                          borderRadius: "4px",
                          fontSize: "11px",
                          fontWeight: "700",
                          backgroundColor: item.isAreaComum
                            ? "#dcfce7"
                            : item.isExtra
                            ? "#fef9c3"
                            : "#e0f2fe",
                          color: item.isAreaComum
                            ? "#15803d"
                            : item.isExtra
                            ? "#854d0e"
                            : "#0369a1",
                        }}
                      >
                        {item.tipoServicoRealizado}
                      </span>
                    </td>
                    <td
                      style={{
                        padding: "12px 14px",
                        textAlign: "right",
                        verticalAlign: "middle",
                      }}
                    >
                      <input
                        type="number"
                        step="0.01"
                        value={
                          tempPrecoBaseManual[item.idChave] !== undefined
                            ? tempPrecoBaseManual[item.idChave]
                            : item.precoBaseManual
                        }
                        placeholder={item.precoBaseMatriz.toFixed(2)}
                        onChange={(e) => {
                          const valor = e.target.value;
                          setTempPrecoBaseManual((prev) => ({
                            ...prev,
                            [item.idChave]: valor,
                          }));
                          salvarComDebounce(
                            debouncePrecoBaseManualRef,
                            item.idChave,
                            valor,
                            onPrecoBaseManualChange
                          );
                        }}
                        style={{
                          width: "70px",
                          padding: "5px",
                          fontSize: "12px",
                          borderRadius: "4px",
                          border: "1px solid #cbd5e1",
                          textAlign: "right",
                          fontWeight: item.precoBaseManual ? "700" : "400",
                        }}
                      />
                      {" €"}
                    </td>
                    <td
                      style={{
                        padding: "12px 14px",
                        textAlign: "right",
                        verticalAlign: "middle",
                        color:
                          item.cobrancaExtraDigitada > 0
                            ? "#b45309"
                            : "#94a3b8",
                      }}
                    >
                      {item.cobrancaExtraDigitada > 0
                        ? `+${item.cobrancaExtraDigitada.toFixed(2)} €`
                        : "0.00 €"}
                    </td>
                    <td
                      style={{
                        padding: "12px 14px",
                        textAlign: "right",
                        fontWeight: "700",
                        color: "#16a34a",
                        verticalAlign: "middle",
                      }}
                    >
                      {item.custoCalculado.toFixed(2)} €
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="cartoes-mobile">
            {dadosFinanceirosOrdenados.map((item) => (
              <CardMobile
                key={`${item.idChave}_${item.id ?? item.idCol ?? ""}`}
                titulo={`${item.apt} · ${item.quarto}`}
                badge={
                  <strong style={{ fontSize: "15px", color: "#16a34a" }}>
                    {item.custoCalculado.toFixed(2)} €
                  </strong>
                }
                resumo={[
                  {
                    label: "Data Fatura",
                    valor: (
                      <input
                        type="date"
                        value={
                          tempDataFatura[item.idChave] !== undefined
                            ? tempDataFatura[item.idChave]
                            : item.dataFaturaManual ||
                              paraInputDate(item.dataFaturaObj)
                        }
                        onChange={(e) => {
                          const valor = e.target.value;
                          setTempDataFatura((prev) => ({
                            ...prev,
                            [item.idChave]: valor,
                          }));
                          salvarComDebounce(
                            debounceDataFaturaRef,
                            item.idChave,
                            valor,
                            onDataFaturaChange
                          );
                        }}
                        style={{
                          padding: "5px",
                          fontSize: "12px",
                          borderRadius: "4px",
                          border: "1px solid #cbd5e1",
                        }}
                      />
                    ),
                  },
                  {
                    label: "Serviço Faturado",
                    valor: (
                      <span
                        style={{
                          padding: "3px 8px",
                          borderRadius: "4px",
                          fontSize: "11px",
                          fontWeight: "700",
                          backgroundColor: item.isAreaComum
                            ? "#dcfce7"
                            : item.isExtra
                            ? "#fef9c3"
                            : "#e0f2fe",
                          color: item.isAreaComum
                            ? "#15803d"
                            : item.isExtra
                            ? "#854d0e"
                            : "#0369a1",
                        }}
                      >
                        {item.tipoServicoRealizado}
                      </span>
                    ),
                  },
                ]}
                detalhes={[
                  {
                    label: "Detalhe/Inquilino",
                    valor: item.isAreaComum
                      ? "Ajuste Contratual Fixado"
                      : item.nome || "-",
                  },
                  {
                    label: "Preço Base",
                    valor: (
                      <span>
                        <input
                          type="number"
                          step="0.01"
                          value={
                            tempPrecoBaseManual[item.idChave] !== undefined
                              ? tempPrecoBaseManual[item.idChave]
                              : item.precoBaseManual
                          }
                          placeholder={item.precoBaseMatriz.toFixed(2)}
                          onChange={(e) => {
                            const valor = e.target.value;
                            setTempPrecoBaseManual((prev) => ({
                              ...prev,
                              [item.idChave]: valor,
                            }));
                            salvarComDebounce(
                              debouncePrecoBaseManualRef,
                              item.idChave,
                              valor,
                              onPrecoBaseManualChange
                            );
                          }}
                          style={{
                            width: "70px",
                            padding: "5px",
                            fontSize: "12px",
                            borderRadius: "4px",
                            border: "1px solid #cbd5e1",
                            textAlign: "right",
                            fontWeight: item.precoBaseManual ? "700" : "400",
                          }}
                        />
                        {" €"}
                      </span>
                    ),
                  },
                  {
                    label: "Extras Declarados",
                    valor:
                      item.cobrancaExtraDigitada > 0
                        ? `+${item.cobrancaExtraDigitada.toFixed(2)} €`
                        : "0.00 €",
                  },
                ]}
              />
            ))}
          </div>
        </>
      )}

      {/* --- SUB-TAB 4: ESTADIAS GAP LONGO --- */}
      {subAbaLimpezas === "gap" && isAdmin && (
        <>
          <div
            style={{
              background: "#eff6ff",
              border: "1px solid #bfdbfe",
              padding: "16px",
              borderRadius: "10px",
              marginBottom: "20px",
            }}
          >
            <strong style={{ color: "#1e40af", fontSize: "13px" }}>
              🔵 O que é um GAP longo?
            </strong>
            <p style={{ margin: "6px 0 0", fontSize: "13px", color: "#334155" }}>
              Estes quartos têm mais de 6 dias entre a saída atual e a próxima
              entrada de Semestre já confirmada. Durante este intervalo, a
              equipa de Sales mantém o quarto aberto no calendário do Airbnb
              para tentar apanhar uma reserva entretanto — por isso a{" "}
              <strong>Preparação sugerida é sempre "Airbnb"</strong>, mesmo
              sabendo que a próxima reserva confirmada é de Semestre. Se o
              quarto ficar preparado como Semestre e cair uma reserva Airbnb,
              o hóspede chega a um quarto preparado incorretamente.
            </p>
          </div>

          <div
            className="tabela-desktop"
            style={{
              background: "#fff",
              borderRadius: "8px",
              overflowX: "auto",
              boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
            }}
          >
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                textAlign: "left",
                fontSize: "13px",
              }}
            >
              <thead>
                <tr
                  style={{
                    backgroundColor: "#eff6ff",
                    borderBottom: "2px solid #bfdbfe",
                  }}
                >
                  <th style={{ padding: "14px 16px", color: "#1e40af" }}>
                    Limpo?
                  </th>
                  <th style={{ padding: "14px 16px", color: "#1e40af" }}>
                    Apartamento
                  </th>
                  <th
                    style={{
                      padding: "14px 16px",
                      color: "#1e40af",
                      textAlign: "center",
                    }}
                  >
                    Quarto
                  </th>
                  <th
                    style={{
                      padding: "14px 16px",
                      color: "#1e40af",
                      textAlign: "center",
                    }}
                  >
                    Saída Atual
                  </th>
                  <th
                    style={{
                      padding: "14px 16px",
                      color: "#1e40af",
                      textAlign: "center",
                    }}
                  >
                    Dias de Intervalo
                  </th>
                  <th style={{ padding: "14px 16px", color: "#1e40af" }}>
                    Próxima Entrada (Semestre)
                  </th>
                  <th style={{ padding: "14px 16px", color: "#1e40af" }}>
                    Tipo Preparação
                  </th>
                  <th
                    style={{
                      padding: "14px 16px",
                      color: "#1e40af",
                      textAlign: "center",
                    }}
                  >
                    Nº Inquilinos
                  </th>
                  <th style={{ padding: "14px 16px", color: "#1e40af" }}>
                    📝 Instruções Coolivin
                  </th>
                  <th
                    style={{
                      padding: "14px 16px",
                      color: "#1e40af",
                      textAlign: "center",
                    }}
                  >
                    Report Olympus
                  </th>
                  <th
                    style={{
                      padding: "14px 16px",
                      color: "#1e40af",
                      textAlign: "center",
                    }}
                  >
                    Ação
                  </th>
                </tr>
              </thead>
              <tbody>
                {dadosGapLongoOrdenados.map((estadia) => {
                  const dataDoc = reportsOlympus[estadia.idChave] || {};
                  const valNotas =
                    tempNotas[estadia.idChave] !== undefined
                      ? tempNotas[estadia.idChave]
                      : notasLimpeza[estadia.idChave] || "";

                  return (
                    <tr
                      key={`${estadia.idChave}_${estadia.id ?? estadia.idCol ?? ""}`}
                      style={{
                        borderBottom: "1px solid #dbeafe",
                        backgroundColor: "#eff6ff",
                      }}
                    >
                      <td style={{ padding: "10px 16px", textAlign: "center" }}>
                        <button
                          onClick={() =>
                            handleToggleEstadoLimpo(
                              estadia.idChave,
                              paraInputDate(estadia.dataSaidaEfetivaObj)
                            )
                          }
                          style={{
                            border: "none",
                            borderRadius: "6px",
                            padding: "6px 12px",
                            fontSize: "11.5px",
                            fontWeight: "bold",
                            cursor: "pointer",
                            backgroundColor: dataDoc.limpo
                              ? "#dcfce7"
                              : "#e2e8f0",
                            color: dataDoc.limpo ? "#15803d" : "#475569",
                          }}
                        >
                          {dataDoc.limpo ? "✅ Limpo" : "⏳ Pendente"}
                        </button>
                      </td>
                      <td style={{ padding: "14px 16px", fontWeight: "bold" }}>
                        {estadia.apt}
                      </td>
                      <td
                        style={{
                          padding: "14px 16px",
                          fontWeight: "bold",
                          textAlign: "center",
                        }}
                      >
                        {estadia.quarto}
                      </td>
                      <td
                        style={{
                          padding: "14px 16px",
                          textAlign: "center",
                          color: "#e11d48",
                          fontWeight: "bold",
                        }}
                      >
                        {estadia.saidaEfetivaTexto}
                      </td>
                      <td
                        style={{
                          padding: "14px 16px",
                          textAlign: "center",
                          fontWeight: "bold",
                          color: "#2563eb",
                        }}
                      >
                        {estadia.DIAS_GAP_LONGO} dias
                      </td>
                      <td style={{ padding: "14px 16px" }}>
                        {estadia.dataCheckinFutura}
                      </td>
                      <td style={{ padding: "10px 16px" }}>
                        {renderTipoPreparacao(estadia)}
                      </td>
                      <td style={{ padding: "10px 16px", textAlign: "center" }}>
                        {renderNumInquilinos(estadia)}
                      </td>
                      <td style={{ padding: "10px 16px" }}>
                        {renderNotas(estadia, valNotas)}
                      </td>
                      <td style={{ padding: "10px 16px", textAlign: "center" }}>
                        {renderReportButton(estadia)}
                      </td>
                      <td style={{ padding: "10px 16px", textAlign: "center" }}>
                        {renderAcaoAdmin(estadia)}
                      </td>
                    </tr>
                  );
                })}
                {dadosGapLongoOrdenados.length === 0 && (
                  <tr>
                    <td
                      colSpan="11"
                      style={{
                        padding: "40px",
                        textAlign: "center",
                        color: "#94a3b8",
                        fontWeight: "600",
                      }}
                    >
                      Sem estadias em GAP longo neste momento. 🎉
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="cartoes-mobile">
            {dadosGapLongoOrdenados.map((estadia) => {
              const dataDoc = reportsOlympus[estadia.idChave] || {};
              const valNotas =
                tempNotas[estadia.idChave] !== undefined
                  ? tempNotas[estadia.idChave]
                  : notasLimpeza[estadia.idChave] || "";

              return (
                <CardMobile
                  key={`${estadia.idChave}_${estadia.id ?? estadia.idCol ?? ""}`}
                  alertaGap
                  titulo={`${estadia.apt} · Quarto ${estadia.quarto}`}
                  badge={
                    <span
                      style={{
                        padding: "4px 10px",
                        borderRadius: "20px",
                        fontSize: "11px",
                        fontWeight: "bold",
                        backgroundColor: "#dbeafe",
                        color: "#1e40af",
                      }}
                    >
                      {estadia.DIAS_GAP_LONGO} dias
                    </span>
                  }
                  acoes={renderAcaoAdmin(estadia)}
                  resumo={[
                    { label: "Saída Atual", valor: estadia.saidaEfetivaTexto },
                    {
                      label: "Limpo?",
                      valor: (
                        <button
                          onClick={() =>
                            handleToggleEstadoLimpo(
                              estadia.idChave,
                              paraInputDate(estadia.dataSaidaEfetivaObj)
                            )
                          }
                          style={{
                            border: "none",
                            borderRadius: "6px",
                            padding: "6px 12px",
                            fontSize: "11.5px",
                            fontWeight: "bold",
                            cursor: "pointer",
                            backgroundColor: dataDoc.limpo
                              ? "#dcfce7"
                              : "#e2e8f0",
                            color: dataDoc.limpo ? "#15803d" : "#475569",
                          }}
                        >
                          {dataDoc.limpo ? "✅ Limpo" : "⏳ Pendente"}
                        </button>
                      ),
                    },
                    {
                      label: "Tipo Preparação",
                      valor: renderTipoPreparacao(estadia),
                    },
                  ]}
                  detalhes={[
                    {
                      label: "Próxima Entrada (Semestre)",
                      valor: estadia.dataCheckinFutura,
                    },
                    {
                      label: "Nº Inquilinos",
                      valor: renderNumInquilinos(estadia),
                    },
                    {
                      label: "Instruções Coolivin",
                      valor: renderNotas(estadia, valNotas),
                      full: true,
                    },
                  ]}
                  footer={renderReportButton(estadia)}
                />
              );
            })}
            {dadosGapLongoOrdenados.length === 0 && (
              <p
                style={{
                  padding: "40px",
                  textAlign: "center",
                  color: "#94a3b8",
                  fontWeight: "600",
                }}
              >
                Sem estadias em GAP longo neste momento. 🎉
              </p>
            )}
          </div>
        </>
      )}

      {/* MODAL REPORT OLYMPUS RESTAURADO (COM SUPORTE A FOTOS DEPOIS E ELIMINAÇÃO) */}
      {modalAberto && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(15, 23, 42, 0.6)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
            backdropFilter: "blur(4px)",
          }}
        >
          <div
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "16px",
              width: "100%",
              maxWidth: "520px",
              maxHeight: "90vh",
              overflowY: "auto",
              padding: "28px",
              boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                marginBottom: "20px",
                borderBottom: "1px solid #f1f5f9",
                paddingBottom: "14px",
              }}
            >
              <span style={{ fontSize: "22px" }}>🛠️</span>
              <h3
                style={{
                  margin: 0,
                  fontSize: "18px",
                  fontWeight: "700",
                  color: "#0f172a",
                }}
              >
                Relatório de Campo Olympus
              </h3>
            </div>

            <div
              style={{
                marginBottom: "20px",
                background: "#fffbeb",
                padding: "14px",
                borderRadius: "10px",
                border: "1px solid #fde68a",
              }}
            >
              <label
                style={{
                  display: "block",
                  fontSize: "12.5px",
                  fontWeight: "800",
                  color: "#b45309",
                  marginBottom: "6px",
                }}
              >
                💰 Cobranças Extra desta Limpeza (€):
              </label>
              <input
                type="number"
                value={modalCustoExtra}
                onChange={(e) => setModalCustoExtra(e.target.value)}
                placeholder="Taxa adicional..."
                style={{
                  width: "100%",
                  padding: "8px",
                  fontSize: "13px",
                  borderRadius: "6px",
                  border: "1px solid #d97706",
                  fontWeight: "700",
                  outline: "none",
                }}
              />
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "12.5px",
                  fontWeight: "700",
                  color: "#475569",
                  marginBottom: "6px",
                }}
              >
                Comentários:
              </label>
              <textarea
                value={modalReportTexto}
                onChange={(e) => setModalReportTexto(e.target.value)}
                style={{
                  width: "100%",
                  minHeight: "100px",
                  padding: "12px",
                  borderRadius: "8px",
                  border: "1px solid #cbd5e1",
                  fontSize: "13px",
                  outline: "none",
                }}
              />
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "12.5px",
                  fontWeight: "700",
                  color: "#475569",
                  marginBottom: "6px",
                }}
              >
                📸 Fotografias - ESTADO ANTES:
              </label>
              <input
                type="file"
                accept="image/*"
                multiple
                id="upload-antes"
                onChange={(e) => handleUploadFoto(e, "antes")}
                style={{ display: "none" }}
              />
              <label
                htmlFor="upload-antes"
                style={{
                  display: "flex",
                  padding: "10px",
                  border: "1px dashed #0284c7",
                  borderRadius: "8px",
                  backgroundColor: "#f0f9ff",
                  color: "#0369a1",
                  fontWeight: "600",
                  fontSize: "12.5px",
                  cursor: "pointer",
                  justifyContent: "center",
                }}
              >
                <span>➕ Carregar Fotos do Estado Inicial</span>
              </label>
              {modalFotosAntes.length > 0 && (
                <div
                  style={{
                    display: "flex",
                    gap: "8px",
                    flexWrap: "wrap",
                    marginTop: "10px",
                  }}
                >
                  {modalFotosAntes.map((img, idx) => (
                    <div
                      key={idx}
                      style={{
                        position: "relative",
                        width: "65px",
                        height: "65px",
                      }}
                    >
                      <img
                        src={img}
                        alt="Foto Antes"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          borderRadius: "6px",
                        }}
                      />
                      <button
                        onClick={() => removerFotoAnexo(idx, "antes")}
                        style={{
                          position: "absolute",
                          top: "-5px",
                          right: "-5px",
                          backgroundColor: "#ef4444",
                          color: "#fff",
                          border: "none",
                          borderRadius: "50%",
                          width: "20px",
                          height: "20px",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          cursor: "pointer",
                          fontWeight: "bold",
                          fontSize: "12px",
                        }}
                      >
                        &times;
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={{ marginBottom: "26px" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "12.5px",
                  fontWeight: "700",
                  color: "#475569",
                  marginBottom: "6px",
                }}
              >
                ✨ Fotografias - RESULTADO DEPOIS:
              </label>
              <input
                type="file"
                accept="image/*"
                multiple
                id="upload-depois"
                onChange={(e) => handleUploadFoto(e, "depois")}
                style={{ display: "none" }}
              />
              <label
                htmlFor="upload-depois"
                style={{
                  display: "flex",
                  padding: "10px",
                  border: "1px dashed #16a34a",
                  borderRadius: "8px",
                  backgroundColor: "#f0fdf4",
                  color: "#15803d",
                  fontWeight: "600",
                  fontSize: "12.5px",
                  cursor: "pointer",
                  justifyContent: "center",
                }}
              >
                <span>➕ Carregar Fotos do Trabalho Finalizado</span>
              </label>
              {modalFotosDepois.length > 0 && (
                <div
                  style={{
                    display: "flex",
                    gap: "8px",
                    flexWrap: "wrap",
                    marginTop: "10px",
                  }}
                >
                  {modalFotosDepois.map((img, idx) => (
                    <div
                      key={idx}
                      style={{
                        position: "relative",
                        width: "65px",
                        height: "65px",
                      }}
                    >
                      <img
                        src={img}
                        alt="Foto Depois"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          borderRadius: "6px",
                        }}
                      />
                      <button
                        onClick={() => removerFotoAnexo(idx, "depois")}
                        style={{
                          position: "absolute",
                          top: "-5px",
                          right: "-5px",
                          backgroundColor: "#ef4444",
                          color: "#fff",
                          border: "none",
                          borderRadius: "50%",
                          width: "20px",
                          height: "20px",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          cursor: "pointer",
                          fontWeight: "bold",
                          fontSize: "12px",
                        }}
                      >
                        &times;
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "flex-end",
                borderTop: "1px solid #f1f5f9",
                paddingTop: "18px",
              }}
            >
              <button
                onClick={() => setModalAberto(false)}
                style={{
                  padding: "9px 18px",
                  backgroundColor: "#fff",
                  border: "1px solid #cbd5e1",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontWeight: "600",
                }}
              >
                Cancelar
              </button>
              <button
                onClick={handleSalvarModalReport}
                style={{
                  padding: "9px 22px",
                  backgroundColor: "#db2777",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  fontWeight: "700",
                  cursor: "pointer",
                }}
              >
                Publicar Dados 📤
              </button>
            </div>
          </div>
        </div>
      )}

      {/* LIGHTBOX CARROSSEL: título Antes/Depois, setas, bolinhas e swipe */}
      {lightboxAberto && lightboxListaFotos.length > 0 && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(15, 23, 42, 0.95)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 100000,
          }}
        >
          <button
            onClick={() => {
              const nomeBase = (lightboxTitulo || "foto")
                .replace(/[^a-zA-Z0-9_-]/g, "_")
                .replace(/_+/g, "_");
              baixarFotoUnica(
                lightboxListaFotos[lightboxIndexAtivo],
                `${nomeBase}_${lightboxIndexAtivo + 1}.jpg`
              );
            }}
            aria-label="Descarregar foto"
            title="Descarregar foto"
            style={{
              position: "absolute",
              top: "20px",
              right: "75px",
              background: "rgba(255, 255, 255, 0.15)",
              border: "none",
              color: "#ffffff",
              fontSize: "20px",
              width: "40px",
              height: "40px",
              borderRadius: "50%",
              cursor: "pointer",
              zIndex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            ⬇️
          </button>

          <button
            onClick={fecharLightbox}
            style={{
              position: "absolute",
              top: "20px",
              right: "25px",
              background: "none",
              border: "none",
              color: "#ffffff",
              fontSize: "36px",
              cursor: "pointer",
              zIndex: 1,
            }}
          >
            &times;
          </button>

          {lightboxTitulo && (
            <div
              style={{
                color: "#ffffff",
                fontWeight: "700",
                fontSize: "15px",
                marginBottom: "12px",
                textAlign: "center",
              }}
            >
              {lightboxTitulo}
              {lightboxListaFotos.length > 1 &&
                ` (${lightboxIndexAtivo + 1}/${lightboxListaFotos.length})`}
            </div>
          )}

          <div
            style={{
              position: "relative",
              maxWidth: "90%",
              maxHeight: "75vh",
              display: "flex",
              alignItems: "center",
              gap: "10px",
            }}
            onTouchStart={handleLightboxTouchStart}
            onTouchEnd={handleLightboxTouchEnd}
          >
            {lightboxListaFotos.length > 1 && (
              <button
                onClick={voltarFotoAnterior}
                aria-label="Foto anterior"
                style={{
                  background: "rgba(255, 255, 255, 0.15)",
                  border: "none",
                  color: "#ffffff",
                  fontSize: "26px",
                  width: "44px",
                  height: "44px",
                  borderRadius: "50%",
                  cursor: "pointer",
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                ‹
              </button>
            )}

            <img
              src={lightboxListaFotos[lightboxIndexAtivo]}
              alt={lightboxTitulo || "Ampliada"}
              style={{
                maxWidth: "100%",
                maxHeight: "75vh",
                objectFit: "contain",
                borderRadius: "8px",
                userSelect: "none",
              }}
            />

            {lightboxListaFotos.length > 1 && (
              <button
                onClick={passarProximaFoto}
                aria-label="Próxima foto"
                style={{
                  background: "rgba(255, 255, 255, 0.15)",
                  border: "none",
                  color: "#ffffff",
                  fontSize: "26px",
                  width: "44px",
                  height: "44px",
                  borderRadius: "50%",
                  cursor: "pointer",
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                ›
              </button>
            )}
          </div>

          {lightboxListaFotos.length > 1 && (
            <div
              style={{
                display: "flex",
                gap: "8px",
                marginTop: "16px",
                flexWrap: "wrap",
                justifyContent: "center",
                maxWidth: "80%",
              }}
            >
              {lightboxListaFotos.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setLightboxIndexAtivo(i)}
                  aria-label={`Ir para a foto ${i + 1}`}
                  style={{
                    width: i === lightboxIndexAtivo ? "11px" : "8px",
                    height: i === lightboxIndexAtivo ? "11px" : "8px",
                    borderRadius: "50%",
                    border: "none",
                    padding: 0,
                    backgroundColor:
                      i === lightboxIndexAtivo
                        ? "#db2777"
                        : "rgba(255, 255, 255, 0.4)",
                    cursor: "pointer",
                  }}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
}
